/**
 * @tt/shell-3d — TRUE-3D character-part catalog
 *
 * The player's lobby icon, rebuilt as real low-poly 3D. The 2D character
 * creator (web/src/features/characters/characterParts.tsx) is a layered
 * face-portrait: a BODY silhouette (the big shape the whole icon reads as —
 * blob / cat / pumpkin / ghost / lantern …), a FACE drawn on it, and a HAT on
 * top, all tinted by the player's palette. This module models EACH of those
 * catalog items as a distinct 3D mesh and assembles them into a head, so the
 * in-game character reads as "that's MY icon" from the top-down camera —
 * same shape, same face, same hat, same colors — instead of a flat avatar
 * texture on a quad.
 *
 * Index ranges mirror the 2D registries exactly (so an icon's body/hat/face
 * index maps to the matching 3D part):
 *   - {@link BODY_COUNT}  = 19  BODY_SHAPES  (0 blob … 18 scroll)
 *   - {@link HAT_COUNT}   = 16  HAT_SHAPES   (0 none … 14 candle-cap, 15 iron helm)
 *   - {@link FACE_COUNT}  = 13  FACE_SHAPES  (0 happy … 12 ghost-veil)
 * Unknown / out-of-range indices wrap (so adding a 2D item never crashes).
 *
 * Geometry is the shell's shared unit primitive set (passed in via
 * {@link CharPartCtx}) so a whole roster is still only a handful of
 * geometries; identity comes from transforms + materials. Each part is added
 * through the host's `part()` so it inherits the toon outline shell, shadow
 * casting, and per-figure disposal for free.
 */
import * as THREE from 'three';
import { PALETTE } from './materials';

/** The shell's shared unit geometries (centered at origin, sized to ~1). */
export interface GeoSet {
  box: THREE.BoxGeometry;
  sph: THREE.SphereGeometry;
  sphLP: THREE.SphereGeometry;
  cyl: THREE.CylinderGeometry;
  cyl8: THREE.CylinderGeometry;
  cone: THREE.ConeGeometry;
  plane: THREE.PlaneGeometry;
}

/** Placement opts for a single mesh (matches the host `part()` signature). */
export interface PartOpts {
  x?: number;
  y?: number;
  z?: number;
  sx?: number;
  sy?: number;
  sz?: number;
  s?: number;
  rx?: number;
  ry?: number;
  rz?: number;
  shadow?: boolean;
  /** Skip the inverted-hull outline shell (emissive flames, eye whites). */
  noShell?: boolean;
}

export type Role = 'primary' | 'accent' | 'skin' | 'dark' | 'stone';

/** Resolved icon colors a 3D head should wear. */
export interface HeadColors {
  /** Body/dome color when the figure represents a lobby icon. */
  head: number;
  primary: number;
  accent: number;
  features: number;
  skin: number;
  shadow: number;
}

/** Material-factory options (mirrors the host `mk()`). */
export interface MkOpts {
  flat?: boolean;
  emissive?: number;
  emi?: number;
  color?: number;
}

/**
 * Builder context the host (figures.ts) hands to every part builder. It wires
 * the catalog into the host's material/outline/disposal system so this module
 * stays pure geometry/assembly with no THREE resource ownership of its own
 * (except `own()`-registered non-shared geometry).
 */
export interface CharPartCtx {
  g: GeoSet;
  /** Toon material in a role (or explicit color). */
  mk: (role: Role, o?: MkOpts) => THREE.MeshToonMaterial;
  /** Add a mesh under `parent` (default core); auto outline shell + shadow. */
  part: (geom: THREE.BufferGeometry, mat: THREE.Material, o: PartOpts, parent?: THREE.Object3D) => THREE.Mesh;
  /** Tracked unlit material (eyes, glows). */
  basic: (color: number, o?: { transparent?: boolean; opacity?: number }) => THREE.MeshBasicMaterial;
  /** Register non-shared geometry for per-figure disposal. */
  own: (geom: THREE.BufferGeometry) => void;
  /** Register a non-toon material (e.g. a particle PointsMaterial) for
   *  per-figure disposal. Optional — builders that only use `mk`/`basic`
   *  (already tracked by the host) can omit it. */
  ownMat?: (mat: THREE.Material) => void;
  /** Register an object that owns GPU buffers of its own (e.g. a particle
   *  {@link THREE.InstancedMesh}, whose per-instance matrix/color buffers are
   *  NOT freed by disposing the shared geometry/material) so the host calls
   *  `.dispose()` on it when the figure is torn down. Optional. */
  ownDisposable?: (d: { dispose: () => void }) => void;
  /**
   * Register a per-frame updater for an ANIMATED head cosmetic (glow pulse,
   * drifting embers, rotating accents, shimmer). The host ticks every
   * registered fn each frame with `(dt, elapsedSeconds)`. This is the head-part
   * analogue of the border-aura `tick` — it lets high-rarity bodies / hats /
   * faces / masks carry signature motion (mythics animate; commons stay still).
   * Keep work cheap: a handful of meshes, transform/emissive only. Optional so
   * non-animating builders simply omit it.
   */
  addFx?: (fn: (dt: number, elapsed: number) => void) => void;
  /** Where head parts attach (the animated `core` group). */
  core: THREE.Object3D;
  colors: HeadColors;
}

export const BODY_COUNT = 19;
export const HAT_COUNT = 16;
export const FACE_COUNT = 13;

/**
 * Cosmetic id registries — these MIRROR the 2D catalog order in
 * web/src/features/characters/characterParts.tsx EXACTLY. The lobby stores
 * `icon.mask` / `icon.border` / `icon.background` as STRING ids (not indices),
 * resolved there with `MASKS.find(m => m.id === icon.mask)`. The 3D builders
 * resolve the same way via {@link resolveCosmetic}: a string id → its array
 * index, an out-of-range / unknown id / undefined → -1 ("none"). Keeping these
 * arrays in sync with the 2D registries is what makes a player's mask/border
 * read as the SAME cosmetic in 3D.
 */
export const MASK_IDS = [
  'domino', 'feline', 'harlequin', 'phantom', 'oni',
  'sunburst', 'void', 'crimson-veil', 'mardi-gras', 'mirror',
] as const;
export const MASK_COUNT = MASK_IDS.length; // 10

export const BORDER_IDS = [
  'leaf-ring', 'brass-frame', 'candle-circle', 'gemstone-studded', 'runic-glyph',
  'ember-ring', 'obsidian-band', 'crystal-shard', 'tide-foam-ring', 'void-eclipse',
  'phoenix-feather', 'gilded-rose-ring',
] as const;
export const BORDER_COUNT = BORDER_IDS.length; // 12

/**
 * Premium body ARCHETYPES (Mythic/Rare) — a SEPARATE index space starting at
 * {@link ARCHETYPE_BASE} (100), mirroring character-render's `drawBody`
 * idx>=100 branches + the locker's BODY_ARCHETYPES. `icon.body` 100-105 routes
 * here instead of into BODY_SHAPES (0-18). Labels match the locker.
 */
export const ARCHETYPE_BASE = 100;
export const ARCHETYPE_LABELS = [
  'floating-orb', 'eight-legged', 'phantom', 'crystal', 'living-flame', 'hexagon',
] as const;
export const ARCHETYPE_COUNT = ARCHETYPE_LABELS.length; // 6

export const BACKGROUND_IDS = [
  'tavern-hearth', 'forest-grove', 'starfield', 'forges-maw',
  'parchment-scroll', 'velvet-drape', 'tide-pool-wash', 'cosmos-veil',
  // 3D-only repurpose targets (no 2D avatar-background twin) — see
  // SEAT_BACKGROUND_ALIAS below. Split out of cosmos-veil/velvet-drape so
  // "Eclipse" and "Arcane circle" carry their OWN readable identity instead
  // of rendering as an indistinguishable reskin of another item.
  'eclipse-veil', 'arcane-circle',
] as const;
export const BACKGROUND_COUNT = BACKGROUND_IDS.length; // 10

/**
 * Ambient PARTICLE cosmetic ids — the 3D drifting-mote system around the
 * character. Covers the avatar `particles` cosmetic kind AND the repurposed
 * lobby seat-particle family (which is being retired as a 2D seat decoration,
 * so its ~14 monetized items live on as character effects). `icon.particles`
 * stores the bare semantic id; unknown / none → nothing. Showcase-only — never
 * spawned in fast gameplay (perf + clutter).
 */
export const PARTICLE_IDS = [
  'embers', 'snow', 'sparks', 'cards', 'fireflies', 'petals', 'bubbles',
  'lightning', 'confetti', 'runes', 'phoenix-feathers', 'shadows', 'coins',
] as const;
export const PARTICLE_COUNT = PARTICLE_IDS.length; // 13

/**
 * REPURPOSE MAP — lobby SEAT-BORDER id → the avatar BORDER aura it renders as
 * in 3D (closest theme). The 2D seat tile is retired; equipping a seat-border
 * now draws the matching ground-aura around the character so the monetized
 * item keeps value. Unknown seat ids fall through to the avatar resolver.
 */
const SEAT_BORDER_ALIAS: Record<string, string> = {
  none: '', ember: 'ember-ring', frost: 'crystal-shard', vine: 'leaf-ring',
  storm: 'void-eclipse', champion: 'gilded-rose-ring', obsidian: 'obsidian-band',
  'gilded-rose': 'gilded-rose-ring', 'wrought-iron': 'brass-frame',
  'crystal-shard': 'crystal-shard', 'solstice-flame': 'ember-ring',
  'runic-engraved': 'runic-glyph', 'tide-foam': 'tide-foam-ring',
  'void-ring': 'void-eclipse',
};

/**
 * REPURPOSE MAP — lobby SEAT-BACKGROUND id → the avatar BACKGROUND backdrop it
 * renders as in 3D (closest theme). Equipping a seat-background draws the
 * matching premium 3D backdrop behind the character.
 */
const SEAT_BACKGROUND_ALIAS: Record<string, string> = {
  plain: 'tavern-hearth', tavern: 'tavern-hearth', moon: 'starfield',
  spice: 'forges-maw', lantern: 'tavern-hearth', arcane: 'arcane-circle',
  parchment: 'parchment-scroll', 'velvet-curtain': 'velvet-drape',
  'forge-glow': 'forges-maw', library: 'parchment-scroll', garden: 'forest-grove',
  cosmos: 'cosmos-veil', 'tide-pool': 'tide-pool-wash', eclipse: 'eclipse-veil',
};

/** Resolve any border id (avatar OR seat) to an avatar BORDER_IDS string. */
function aliasBorderId(id: number | string | undefined): number | string | undefined {
  if (typeof id !== 'string') return id;
  if (id in SEAT_BORDER_ALIAS) return SEAT_BORDER_ALIAS[id];
  return id;
}
/** Resolve any background id (avatar OR seat) to an avatar BACKGROUND_IDS string. */
function aliasBackgroundId(id: number | string | undefined): number | string | undefined {
  if (typeof id !== 'string') return id;
  if (id in SEAT_BACKGROUND_ALIAS) return SEAT_BACKGROUND_ALIAS[id];
  return id;
}

/**
 * Resolve a lobby cosmetic field (`icon.mask` etc.) to a registry INDEX, or
 * -1 for "none". Accepts the string id the lobby actually stores, OR a raw
 * numeric index (used by the catalog renderer to sweep every entry). Unknown
 * ids and out-of-range numbers map to -1 so an unrecognised cosmetic simply
 * renders nothing instead of crashing — additive-safe as the 2D catalog grows.
 */
export function resolveCosmetic(
  value: number | string | undefined | null,
  ids: readonly string[],
  count: number,
): number {
  if (value === undefined || value === null || value === '') return -1;
  if (typeof value === 'number') return value >= 0 && value < count ? value : -1;
  return ids.indexOf(value); // -1 when unknown
}

/** Where the face features + hat anchor on a built head. */
export interface HeadAnchor {
  /** World-Y of the crown (a hat sits here). */
  top: number;
  /** Face-plane center Y. */
  faceY: number;
  /** Face-plane forward Z (front surface). */
  faceZ: number;
  /** Feature scale multiplier (small heads shrink eyes/mouth). */
  faceScale: number;
  /**
   * Radians the face plane is pitched UP toward the camera (about X). The
   * game's camera is a steep ~57°-above-horizon top-down; a face built flat on
   * the head's vertical front is grazing/edge-on from up there and reads as
   * "no face". Pitching the whole feature cluster up so its normal points
   * up-and-forward puts the eyes/mouth square to the camera. Default 0.7
   * (~40°) — a compromise that stays readable in flatter head-on shots too.
   */
  faceTilt?: number;
}

/** Default face pitch toward the steep top-down camera (radians). */
const FACE_TILT = 0.85;
/**
 * Global feature-size gain. Faces built at the per-head `faceScale` were
 * legible head-on but too small to read from the steep top-down camera once
 * the head also tips back; this fattens every eye/brow/mouth uniformly so the
 * face stays the icon's identity from above. Tuned against the QA shots.
 */
const FACE_GAIN = 1.22;

const wrap = (i: number, n: number): number => ((i % n) + n) % n;

// ───────────────────────── face features ─────────────────────────

/**
 * One unlit cartoon eye (white sclera + dark pupil) at LOCAL (x,y,z) under
 * `parent`, facing the parent's +Z. (Face features hang under a pitched-up
 * group, so +Z there points up-and-forward at the camera.)
 */
export function addEye(
  b: CharPartCtx,
  parent: THREE.Object3D,
  x: number,
  y: number,
  z: number,
  w: number,
  h: number,
  pupil = 0x141016,
): void {
  const white = b.basic(0xf4f0e8);
  const dark = b.basic(pupil);
  const m = Math.min(w, h);
  b.part(b.g.sph, white, { x, y, z, sx: w, sy: h, sz: m * 0.6, noShell: true, shadow: false }, parent);
  const pr = m * 0.58;
  // Pupil hugs the FRONT of the eye-white (its z-radius is 0.5*m*0.6 = 0.3*m)
  // rather than floating well ahead of it — a too-large forward offset was a
  // big part of the "eyes hover in front of the face" read.
  b.part(b.g.sph, dark, { x, y, z: z + m * 0.26, sx: pr, sy: pr, sz: pr * 0.55, noShell: true, shadow: false }, parent);
  // Part A mastering: a tiny CATCH-LIGHT glint on the pupil. One unlit white
  // speck proud of the eye gives every common face a little glossy life +
  // material depth (the eyes read as wet/round, not flat dots) — a baseline-
  // GOOD upgrade that costs nothing and stays well clear of the rarity-4/5
  // signature treatments. Seated on the pupil front, so the eyes-seated fix is
  // untouched.
  const hl = b.basic(0xffffff);
  b.part(b.g.sph, hl, { x: x - w * 0.2, y: y + h * 0.22, z: z + m * 0.34, s: m * 0.16, noShell: true, shadow: false }, parent);
}

/** A small relief mouth (feature ink) under `parent`, on its +Z face. */
function addMouth(
  b: CharPartCtx,
  parent: THREE.Object3D,
  y: number,
  z: number,
  width: number,
  kind: 'smile' | 'flat' | 'frown',
): void {
  const mat = b.mk('dark', { color: b.colors.features, flat: false });
  if (kind === 'flat') {
    b.part(b.g.box, mat, { y, z, sx: width, sy: 0.03, sz: 0.04, noShell: true }, parent);
    return;
  }
  const tilt = kind === 'smile' ? 0.5 : -0.5;
  const dy = kind === 'smile' ? -0.03 : 0.03;
  b.part(b.g.box, mat, { y, z, sx: width * 0.5, sy: 0.03, sz: 0.04, noShell: true }, parent);
  b.part(b.g.box, mat, { x: -width * 0.32, y: y + dy, z, sx: width * 0.36, sy: 0.03, sz: 0.04, rz: tilt, noShell: true }, parent);
  b.part(b.g.box, mat, { x: width * 0.32, y: y + dy, z, sx: width * 0.36, sy: 0.03, sz: 0.04, rz: -tilt, noShell: true }, parent);
}

/**
 * Build the relief face features for FACE_SHAPES index `fi` at the head's
 * anchor. Mirrors the 2D FACE_SHAPES order (0 happy … 12 ghost-veil).
 *
 * VISIBILITY: every feature hangs under a single `face` group anchored at the
 * head's front-upper surface and PITCHED UP toward the camera by
 * `a.faceTilt` (so the eyes/mouth normal points up-and-forward, square to the
 * steep top-down view instead of edge-on). Coordinates below are LOCAL to that
 * group: +y = up the face, +z = proud of the head surface; the group's tilt
 * does the rest. This is the fix for "faces don't show from above".
 */
export function buildFaceFeatures(b: CharPartCtx, fi: number, a: HeadAnchor): void {
  const idx = wrap(fi, FACE_COUNT);
  const sc = a.faceScale * FACE_GAIN;
  // Pitched-up face plane: pivot at the head's front-upper surface, tipped so
  // its +Z normal aims up toward the camera. All features are children of it.
  const face = new THREE.Group();
  face.position.set(0, a.faceY, a.faceZ);
  face.rotation.x = -(a.faceTilt ?? FACE_TILT);
  b.core.add(face);
  // Local frame: eyes a touch above center, mouth below; z seated on the dome.
  const ey = 0.02 * sc; // eye row, local-Y
  // The feature group's origin sits at the head's front-CENTRE surface, but the
  // head is a SPHERE: at the eyes' lateral offset (±~0.11) the real surface has
  // already curved back ~0.03-0.05. A positive local-z therefore stacked ON TOP
  // of that recession and the features floated off the face. Seat them just into
  // the dome instead (slightly negative) so the eyeballs nestle on the curved
  // surface and read as part of the face, not goggles hovering in front of it.
  const z = -0.02; // local-Z, just into the head surface (was +0.055 → floated)
  const featMat = (extra: MkOpts = {}): THREE.MeshToonMaterial => b.mk('dark', { color: b.colors.features, ...extra });
  switch (idx) {
    case 1: // sleepy — low drowsy lids
      addEye(b, face, -0.085 * sc, ey, z, 0.11 * sc, 0.045 * sc);
      addEye(b, face, 0.085 * sc, ey, z, 0.11 * sc, 0.045 * sc);
      addMouth(b, face, -0.11 * sc, z, 0.1 * sc, 'flat');
      break;
    case 2: // wide — surprised
      addEye(b, face, -0.1 * sc, ey, z, 0.13 * sc, 0.13 * sc);
      addEye(b, face, 0.1 * sc, ey, z, 0.13 * sc, 0.13 * sc);
      addMouth(b, face, -0.12 * sc, z, 0.08 * sc, 'flat');
      break;
    case 3: // stern — slits + angled brows
      addEye(b, face, -0.085 * sc, ey - 0.01, z, 0.1 * sc, 0.06 * sc);
      addEye(b, face, 0.085 * sc, ey - 0.01, z, 0.1 * sc, 0.06 * sc);
      b.part(b.g.box, featMat(), { x: -0.09 * sc, y: ey + 0.08 * sc, z, sx: 0.13 * sc, sy: 0.03, sz: 0.04, rz: -0.4, noShell: true }, face);
      b.part(b.g.box, featMat(), { x: 0.09 * sc, y: ey + 0.08 * sc, z, sx: 0.13 * sc, sy: 0.03, sz: 0.04, rz: 0.4, noShell: true }, face);
      addMouth(b, face, -0.12 * sc, z, 0.11 * sc, 'frown');
      break;
    case 4: { // cute — round eyes + blush + tiny smile
      addEye(b, face, -0.09 * sc, ey, z, 0.11 * sc, 0.11 * sc);
      addEye(b, face, 0.09 * sc, ey, z, 0.11 * sc, 0.11 * sc);
      for (const bx of [-0.17 * sc, 0.17 * sc]) {
        const bm = b.basic(b.colors.accent, { transparent: true, opacity: 0.55 });
        b.part(b.g.sph, bm, { x: bx, y: -0.06 * sc, z: z - 0.02, sx: 0.07 * sc, sy: 0.05 * sc, sz: 0.04, noShell: true, shadow: false }, face);
      }
      addMouth(b, face, -0.12 * sc, z, 0.07 * sc, 'smile');
      break;
    }
    case 5: // mischief — eyes + a lopsided smirk
      addEye(b, face, -0.085 * sc, ey, z, 0.1 * sc, 0.09 * sc);
      addEye(b, face, 0.085 * sc, ey, z, 0.1 * sc, 0.09 * sc);
      b.part(b.g.box, featMat(), { x: 0.02 * sc, y: -0.12 * sc, z, sx: 0.12 * sc, sy: 0.03, sz: 0.04, rz: 0.4, noShell: true }, face);
      break;
    case 6: // sad — eyes + frown
      addEye(b, face, -0.085 * sc, ey, z, 0.1 * sc, 0.1 * sc);
      addEye(b, face, 0.085 * sc, ey, z, 0.1 * sc, 0.1 * sc);
      addMouth(b, face, -0.13 * sc, z, 0.1 * sc, 'frown');
      break;
    case 7: // cyclops — one big central eye
      addEye(b, face, 0, ey, z, 0.18 * sc, 0.18 * sc);
      addMouth(b, face, -0.14 * sc, z, 0.1 * sc, 'flat');
      break;
    case 8: { // searing-mark (MYTHIC) — molten brand: pulsing ember eyes + a
      //        glowing rune cross + drifting sparks. Demonic, clearly mythic.
      const eyeMat = b.mk('dark', { color: PALETTE.emberHigh, emissive: PALETTE.emberMid, emi: 1.4, flat: false });
      for (const sgn of [-1, 1])
        b.part(b.g.sph, eyeMat, { x: sgn * 0.085 * sc, y: ey, z, sx: 0.075 * sc, sy: 0.07 * sc, sz: 0.05, noShell: true, shadow: false }, face);
      const brandMat = b.mk('dark', { color: PALETTE.gold, emissive: PALETTE.emberHigh, emi: 1.0 });
      b.part(b.g.box, brandMat, { x: 0.04 * sc, y: ey - 0.02 * sc, z: z + 0.01, sx: 0.028, sy: 0.26 * sc, sz: 0.04, rz: 0.12, noShell: true }, face); // vertical brand
      b.part(b.g.box, brandMat, { x: 0.04 * sc, y: ey + 0.05 * sc, z: z + 0.01, sx: 0.11 * sc, sy: 0.026, sz: 0.04, rz: 0.12, noShell: true }, face); // crossbar
      const sparks = [-0.1, 0.06, 0.16].map((dx, i) => {
        const mat = b.basic(PALETTE.emberHigh, { transparent: true, opacity: 0.9 });
        const mesh = b.part(b.g.sph, mat, { x: dx * sc, y: ey + 0.12 * sc, z: z + 0.02, s: 0.02 * sc, noShell: true, shadow: false }, face);
        return { mesh, mat, phase: i / 3 };
      });
      b.addFx?.((_dt, t) => {
        eyeMat.emissiveIntensity = 1.1 + 0.6 * Math.sin(t * 3.2);
        brandMat.emissiveIntensity = 0.8 + 0.5 * Math.sin(t * 3.2 + 0.8);
        for (const s of sparks) {
          const p = (t * 0.45 + s.phase) % 1;
          s.mesh.position.y = ey + 0.1 * sc + p * 0.18 * sc;
          s.mat.opacity = 0.85 * (1 - p);
        }
      });
      break;
    }
    case 9: // single-eye — one eye, slightly off-center
      addEye(b, face, 0.02 * sc, ey, z, 0.15 * sc, 0.15 * sc);
      break;
    case 10: { // third-eye (EPIC) — two eyes + an arcane GLOWING third-eye gem
      //          (emissive, pulsing) flanked by small sigil dots.
      addEye(b, face, -0.09 * sc, ey - 0.02, z, 0.1 * sc, 0.1 * sc);
      addEye(b, face, 0.09 * sc, ey - 0.02, z, 0.1 * sc, 0.1 * sc);
      const thirdMat = b.mk('dark', { color: b.colors.accent, emissive: b.colors.accent, emi: 1.0, flat: false });
      b.part(b.g.sph, thirdMat, { x: 0, y: ey + 0.13 * sc, z: z + 0.01, sx: 0.08 * sc, sy: 0.09 * sc, sz: 0.05, noShell: true, shadow: false }, face);
      for (const sgn of [-1, 1])
        b.part(b.g.sph, b.basic(b.colors.accent), { x: sgn * 0.13 * sc, y: ey + 0.13 * sc, z, s: 0.018 * sc, noShell: true, shadow: false }, face);
      addMouth(b, face, -0.12 * sc, z, 0.08 * sc, 'flat');
      b.addFx?.((_dt, t) => { thirdMat.emissiveIntensity = 0.7 + 0.5 * (0.5 + 0.5 * Math.sin(t * 2.6)); });
      break;
    }
    case 11: // calligraphic — thin elegant eyes + soft smile
      addEye(b, face, -0.1 * sc, ey, z, 0.13 * sc, 0.04 * sc);
      addEye(b, face, 0.1 * sc, ey, z, 0.13 * sc, 0.04 * sc);
      addMouth(b, face, -0.12 * sc, z, 0.09 * sc, 'smile');
      break;
    case 12: { // ghost-veil (EPIC) — hollow dark sockets with a faint COLD glow
      //          floating deep inside, pulsing. Eerie wraith read, not flat eyes.
      for (const sgn of [-1, 1])
        b.part(b.g.sph, b.basic(0x05040a), { x: sgn * 0.09 * sc, y: ey, z, sx: 0.12 * sc, sy: 0.13 * sc, sz: 0.05, noShell: true, shadow: false }, face);
      const glowMat = b.basic(0xaad8ff, { transparent: true, opacity: 0.5 });
      for (const sgn of [-1, 1])
        b.part(b.g.sph, glowMat, { x: sgn * 0.09 * sc, y: ey, z: z + 0.02, s: 0.035 * sc, noShell: true, shadow: false }, face);
      b.addFx?.((_dt, t) => { glowMat.opacity = 0.2 + 0.32 * (0.5 + 0.5 * Math.sin(t * 1.7)); });
      break;
    }
    case 0: // happy (default) — round eyes + big smile
    default:
      addEye(b, face, -0.09 * sc, ey, z, 0.11 * sc, 0.11 * sc);
      addEye(b, face, 0.09 * sc, ey, z, 0.11 * sc, 0.11 * sc);
      addMouth(b, face, -0.12 * sc, z, 0.1 * sc, 'smile');
      break;
  }
}

// ───────────────────────── hats ─────────────────────────

const ringOf = (n: number, r: number, build: (x: number, z: number, a: number) => void): void => {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    build(Math.sin(a) * r, Math.cos(a) * r, a);
  }
};

/**
 * Build a real low-poly hat for HAT_SHAPES index `hi` resting on a crown at
 * world-Y `top`. Mirrors the 2D HAT_SHAPES order (0 none … 14 candle-cap),
 * tinted by the icon's accent/feature colors.
 */
export function buildHat(b: CharPartCtx, hi: number, top: number): void {
  const idx = wrap(hi, HAT_COUNT);
  const T = top;
  const GOLD = PALETTE.gold;
  const BONE = 0xc7a878;
  const part = b.part;
  const g = b.g;
  switch (idx) {
    case 0: // none
      break;
    case 1: // wizard — tall slender cone + gold tip
      part(g.cone, b.mk('accent'), { y: T + 0.34, z: -0.01, sx: 0.34, sy: 0.66, sz: 0.34 });
      part(g.sph, b.mk('dark', { color: GOLD, emissive: GOLD, emi: 0.4 }), { y: T + 0.64, s: 0.09 });
      break;
    case 2: // crown — gold band + five points
      part(g.cyl8, b.mk('dark', { color: GOLD }), { y: T + 0.05, sx: 0.42, sy: 0.16, sz: 0.42 });
      ringOf(5, 0.17, (x, z) => part(g.cone, b.mk('dark', { color: GOLD }), { x, z, y: T + 0.18, s: 0.11, sy: 0.22 }));
      break;
    case 3: // top hat — stovepipe + (narrow) brim + accent band. Brim kept
      // just over the head and lifted so it doesn't umbrella the face from the
      // steep top-down camera (a wide brim hides the whole head from above).
      part(g.cyl8, b.mk('dark'), { y: T + 0.34, sx: 0.34, sy: 0.46, sz: 0.34 });
      part(g.cyl8, b.mk('dark'), { y: T + 0.16, sx: 0.42, sy: 0.05, sz: 0.42 });
      part(g.cyl8, b.mk('accent'), { y: T + 0.2, sx: 0.36, sy: 0.08, sz: 0.36 });
      break;
    case 4: // hood — dome shell pushed onto the BACK/top so the face stays clear,
      //         now with a deeper-shade inner LINING rim peeking at the opening
      //         (Part A: reads as a real cowl with depth, not one flat dome).
      part(g.sph, b.mk('accent', { flat: false }), { y: T + 0.04, z: -0.22, sx: 0.46, sy: 0.48, sz: 0.46 });
      part(g.sph, b.mk('dark', { color: shadeHex(b.colors.accent, 0.42), flat: false }), { y: T + 0.0, z: -0.13, sx: 0.4, sy: 0.42, sz: 0.4, noShell: true });
      break;
    case 5: // antlers — branching bony cones
      for (const sgn of [-1, 1]) {
        part(g.cone, b.mk('dark', { color: BONE }), { x: sgn * 0.14, y: T + 0.2, z: -0.04, sx: 0.07, sy: 0.42, sz: 0.07, rz: sgn * 0.5 });
        part(g.cone, b.mk('dark', { color: BONE }), { x: sgn * 0.3, y: T + 0.34, z: -0.04, sx: 0.05, sy: 0.22, sz: 0.05, rz: sgn * 0.95 });
      }
      break;
    case 6: // flower — gold center + accent petals
      part(g.sph, b.mk('dark', { color: GOLD }), { y: T + 0.12, s: 0.1 });
      ringOf(5, 0.12, (x, z) => part(g.sph, b.mk('accent'), { x, z, y: T + 0.12, s: 0.11, sy: 0.05 }));
      break;
    case 7: // candle — wax pillar + emissive flame
      part(g.cyl, b.mk('dark', { color: PALETTE.parchment }), { y: T + 0.18, sx: 0.12, sy: 0.34, sz: 0.12 });
      part(g.cone, b.mk('dark', { color: PALETTE.emberHigh, emissive: PALETTE.emberMid, emi: 1.5 }), { y: T + 0.42, sx: 0.08, sy: 0.18, sz: 0.08, noShell: true });
      break;
    case 8: { // crown-of-coals (MYTHIC) — black-iron crown of ember-tipped
      //         spires with smouldering coals nestled between, all pulsing.
      //         A true legendary crown, not a flat band of dots.
      part(g.cyl8, b.mk('dark', { color: 0x1a1410 }), { y: T + 0.06, sx: 0.44, sy: 0.18, sz: 0.44 }); // iron band
      const spireMat = b.mk('dark', { color: 0x2a1e16 });
      const ember = b.mk('dark', { color: PALETTE.emberHigh, emissive: PALETTE.emberMid, emi: 1.5, flat: false });
      const coal = b.mk('dark', { color: PALETTE.emberMid, emissive: PALETTE.emberLow, emi: 1.0, flat: false });
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        const x = Math.sin(a) * 0.3;
        const z = Math.cos(a) * 0.3;
        part(g.cone, spireMat, { x, z, y: T + 0.24, sx: 0.08, sy: 0.34, sz: 0.08 }); // tall spire
        part(g.sph, ember, { x, z, y: T + 0.42, s: 0.075, noShell: true }); // ember tip
        const a2 = a + Math.PI / 5;
        part(g.sph, coal, { x: Math.sin(a2) * 0.3, z: Math.cos(a2) * 0.3, y: T + 0.13, s: 0.07, noShell: true }); // coal between
      }
      b.addFx?.((_dt, t) => {
        ember.emissiveIntensity = 1.2 + 0.55 * Math.sin(t * 3);
        coal.emissiveIntensity = 0.75 + 0.45 * Math.sin(t * 3 + 1.6);
      });
      break;
    }
    case 9: // tricorn — brim (trimmed + lifted) + low crown + three peaks
      part(g.cyl8, b.mk('accent'), { y: T + 0.14, sx: 0.43, sy: 0.06, sz: 0.43 });
      part(g.sph, b.mk('accent'), { y: T + 0.23, s: 0.32, sy: 0.24 });
      ringOf(3, 0.24, (x, z) => part(g.cone, b.mk('dark'), { x, z, y: T + 0.2, s: 0.12, sy: 0.18 }));
      break;
    case 10: // witch — brim (trimmed + lifted) + tall cone + band. A full-width
      // brim turns the head into an umbrella from above; keep it just past the
      // head and raised so the head + face read below it.
      part(g.cyl8, b.mk('accent'), { y: T + 0.15, sx: 0.43, sy: 0.05, sz: 0.43 });
      part(g.cone, b.mk('accent'), { y: T + 0.5, sx: 0.3, sy: 0.72, sz: 0.3 });
      part(g.cyl8, b.mk('dark'), { y: T + 0.24, sx: 0.3, sy: 0.07, sz: 0.3 });
      break;
    case 11: { // brass goggles — enlarged: a strap BAND wrapping the head +
      //          two proper round brass-rimmed lenses with an emissive amber
      //          glass glint each. The old lenses were tiny (s 0.11, no
      //          glass/glint) and barely visible against the head; this
      //          scales them up and gives the glass an actual amber glow.
      part(g.cyl8, b.mk('dark'), { y: T - 0.02, z: 0.02, sx: 0.46, sy: 0.09, sz: 0.46 }); // strap band around the head
      for (const sgn of [-1, 1]) {
        part(g.cyl, b.mk('dark', { color: PALETTE.brass }), { x: sgn * 0.16, y: T - 0.02, z: 0.24, sx: 0.16, sy: 0.07, sz: 0.16, rx: Math.PI / 2 }); // brass lens rim
        part(g.cyl, b.mk('dark', { color: 0xffb347, emissive: 0xffb347, emi: 0.75, flat: false }), { x: sgn * 0.16, y: T - 0.02, z: 0.27, sx: 0.11, sy: 0.03, sz: 0.11, rx: Math.PI / 2, noShell: true }); // amber glass glint
      }
      break;
    }
    case 12: // antler circlet — gold band + small antlers
      part(g.cyl8, b.mk('dark', { color: GOLD }), { y: T + 0.02, sx: 0.42, sy: 0.07, sz: 0.42 });
      for (const sgn of [-1, 1]) {
        part(g.cone, b.mk('dark', { color: BONE }), { x: sgn * 0.16, y: T + 0.18, z: -0.02, sx: 0.06, sy: 0.3, sz: 0.06, rz: sgn * 0.45 });
      }
      break;
    case 13: { // halo (EPIC) — twin emissive gold rings + orbiting light motes,
      //         gently bobbing & rotating (animated). Richer than a lone ring.
      const grp = new THREE.Group();
      b.core.add(grp);
      const haloGeo = new THREE.TorusGeometry(0.2, 0.034, 8, 24);
      b.own(haloGeo);
      const innerGeo = new THREE.TorusGeometry(0.125, 0.015, 8, 20);
      b.own(innerGeo);
      const ringMat = b.mk('dark', { color: GOLD, emissive: GOLD, emi: 1.2 });
      part(haloGeo, ringMat, { y: T + 0.34, rx: Math.PI / 2, noShell: true, shadow: false }, grp);
      part(innerGeo, b.mk('dark', { color: PALETTE.emberHigh, emissive: PALETTE.emberHigh, emi: 0.9 }), { y: T + 0.34, rx: Math.PI / 2, noShell: true, shadow: false }, grp);
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        part(g.sph, b.basic(0xfff4c0, { transparent: true, opacity: 0.9 }), { x: Math.sin(a) * 0.2, z: Math.cos(a) * 0.2, y: T + 0.34, s: 0.028, noShell: true, shadow: false }, grp);
      }
      b.addFx?.((_dt, t) => {
        grp.rotation.y = t * 0.7; // motes orbit
        grp.position.y = Math.sin(t * 1.6) * 0.03; // gentle bob
        ringMat.emissiveIntensity = 1.0 + 0.3 * Math.sin(t * 2.4);
      });
      break;
    }
    case 14: // candle cap — skullcap + wax candle + flame
      part(g.sph, b.mk('accent'), { y: T + 0.0, s: 0.4, sy: 0.22 });
      part(g.cyl, b.mk('dark', { color: PALETTE.parchment }), { y: T + 0.22, sx: 0.1, sy: 0.26, sz: 0.1 });
      part(g.cone, b.mk('dark', { color: PALETTE.emberHigh, emissive: PALETTE.emberMid, emi: 1.5 }), { y: T + 0.42, sx: 0.07, sy: 0.14, sz: 0.07, noShell: true });
      break;
    case 15: { // iron helm — an OPEN-FACE barbute: a steel dome skull, a raised
      //          brow ridge, a short flared neck guard at the BACK, cheek plates
      //          at the sides, a central nasal bar, and an accent plume crest.
      //          Kept open at the front (like the hood/tricorn) so the face
      //          still reads under the steep top-down camera — never umbrellas
      //          the head. Steel signature colors (not palette) so it always
      //          reads as forged iron; plume + rivets take the player accent so
      //          it ties into the Steel-palette set.
      //          Brightened from a muddy grey (0x9aa4b2) to a properly
      //          polished steel, with a BRASS nasal guard + plume socket
      //          (the previous version used plain steel for both, losing the
      //          "trim" read) and a subtle emissive slit glow hinting at
      //          eyes within the visor.
      const steel = 0xb8c4d4;
      const steelDk = 0x7a8494;
      // domed skull — sphere squashed slightly, sitting on the crown
      part(g.sph, b.mk('dark', { color: steel, flat: false }), { y: T + 0.08, z: -0.03, sx: 0.44, sy: 0.42, sz: 0.46 });
      // raised brow ridge band across the front
      part(g.cyl8, b.mk('dark', { color: steelDk }), { y: T - 0.02, z: 0.02, sx: 0.45, sy: 0.1, sz: 0.47 });
      // brow rivets in accent
      for (const sx of [-0.24, 0, 0.24])
        part(g.sph, b.mk('accent', { emissive: b.colors.accent, emi: 0.3 }), { x: sx, y: T - 0.02, z: 0.24, s: 0.04, noShell: true });
      // short neck guard hugging the back of the skull (never over the face)
      part(g.sphLP, b.mk('dark', { color: steelDk }), { y: T - 0.04, z: -0.26, sx: 0.34, sy: 0.24, sz: 0.2 });
      // cheek plates hugging the sides (vertical, flush to the head)
      for (const sgn of [-1, 1])
        part(g.box, b.mk('dark', { color: steelDk }), { x: sgn * 0.3, y: T - 0.14, z: 0.08, sx: 0.07, sy: 0.22, sz: 0.26 });
      // subtle emissive eye-slit glow — hints at eyes within the visor
      part(g.box, b.mk('dark', { color: 0xffb347, emissive: 0xffb347, emi: 0.55, flat: false }), { y: T - 0.11, z: 0.3, sx: 0.2, sy: 0.03, sz: 0.02, noShell: true });
      // BRASS nasal bar down the bridge (thin — leaves eyes visible)
      part(g.box, b.mk('dark', { color: PALETTE.brass }), { y: T - 0.16, z: 0.3, sx: 0.05, sy: 0.28, sz: 0.06 });
      // accent plume crest — a low fin over the dome (front→back), sitting tight
      part(g.box, b.mk('accent'), { y: T + 0.3, z: -0.02, sx: 0.05, sy: 0.1, sz: 0.34 });
      part(g.cyl8, b.mk('dark', { color: PALETTE.brass }), { y: T + 0.22, z: 0.08, sx: 0.08, sy: 0.08, sz: 0.08 }); // brass plume socket
      break;
    }
  }
}

// ───────────────────────── masks (face overlays) ─────────────────────────

/**
 * Build the 3D MASK for `mask` (a lobby mask id string like `'oni'`, or a raw
 * index for the catalog) on the head. Mirrors the 2D MASKS order (0 domino …
 * 9 mirror); unknown / none → nothing.
 *
 * TRUE-3D, not a decal. The earlier version laid each mask out as a near-flat
 * squashed-sphere PLATE (depth ≈ 0.06) floating proud of the face — from the
 * steep gameplay camera that read as a 2D sticker pasted on the head. This
 * builds every mask from genuinely 3D forms instead:
 *  - the base is a {@link cap}: a CURVED dome (real forward bulge ~0.17) whose
 *    back half is buried in the head, so it wraps the head's curve, catches the
 *    key light across its top, casts/receives shadow, and reads as a sculpted
 *    shell ON the face — never a flat plate.
 *  - eyes sit in RECESSED dark sockets (a real hole in the mask) with the eye
 *    proud of it, so the face still reads through the mask.
 *  - horns / ears / rays / plumes / veil-folds are real cones/forms with DEPTH,
 *    tilted OUT of the face plane into space (not painted on the plate).
 * Everything hangs under the same pitched-up group the face features use, so
 * the mask tilts toward the camera with the head. Themed signature colors stay
 * fixed (crimson oni, gold sunburst, silver mirror …) for recognisability;
 * ribbon/ear accents wear the player's accent color where the 2D mask does.
 */
export function buildMask(b: CharPartCtx, mask: number | string | undefined, a: HeadAnchor): void {
  const idx = resolveCosmetic(mask, MASK_IDS, MASK_COUNT);
  if (idx < 0) return;
  const sc = a.faceScale * FACE_GAIN;
  const m = new THREE.Group();
  m.position.set(0, a.faceY, a.faceZ);
  m.rotation.x = -(a.faceTilt ?? FACE_TILT);
  b.core.add(m);
  const ey = 0.02 * sc; // eye row (matches buildFaceFeatures)
  const z = 0.075; // FRONT reference: the mask cap bulges OUT to about here
  const acc = b.colors.accent;
  const DEPTH = 0.17; // default forward bulge of a mask cap (the 3D-ness)

  /**
   * A CURVED mask cap — the 3D base every mask is built on. A sphere scaled
   * (w, h, depth) and pushed back so its FRONT lands at `fz` while its rear
   * half sinks into the head: it bulges off the face as a sculpted dome (its
   * normal sweeps across it → a real toon light gradient, not one flat tone)
   * and keeps its outline shell + shadow. This is the fix for "2D sticker".
   */
  const cap = (
    color: number,
    w: number,
    h: number,
    o: { x?: number; y?: number; z?: number; depth?: number; flat?: boolean; emissive?: number; emi?: number } = {},
  ): THREE.Mesh => {
    const depth = o.depth ?? DEPTH;
    const fz = o.z ?? z;
    return b.part(
      b.g.sph,
      b.mk('dark', { color, flat: o.flat ?? false, ...(o.emissive !== undefined ? { emissive: o.emissive, emi: o.emi } : {}) }),
      { x: o.x ?? 0, y: o.y ?? ey, z: fz - depth / 2, sx: w, sy: h, sz: depth },
      m,
    );
  };
  // recessed dark eye SOCKETS — a real hole punched in the mask the eyes sit in.
  const sockets = (w = 0.06): void => {
    for (const sgn of [-1, 1])
      b.part(b.g.sph, b.basic(0x06040b), { x: sgn * 0.1 * sc, y: ey, z: z - 0.04, sx: w * sc * 1.6, sy: w * sc * 1.6, sz: 0.06, noShell: true, shadow: false }, m);
  };
  const eyeZ = z + 0.02; // eyes proud of the socket so they read from above
  // two white "eyes through the holes"
  const whiteEyes = (): void => {
    sockets(0.05);
    for (const sgn of [-1, 1]) addEye(b, m, sgn * 0.1 * sc, ey, eyeZ, 0.055 * sc, 0.055 * sc);
  };
  // two themed glowing eye-slots, set into sockets (oni / sunburst / void / veil)
  const glowEyes = (color: number, w = 0.05): void => {
    sockets(w);
    for (const sgn of [-1, 1])
      b.part(b.g.sph, b.mk('dark', { color, emissive: color, emi: 0.75, flat: false }), { x: sgn * 0.1 * sc, y: ey, z: eyeZ, sx: w * sc, sy: w * sc * 1.05, sz: w * sc * 0.85, noShell: true, shadow: false }, m);
  };
  // a small faceted 3D gem/stud (flat-shaded so its facets catch light)
  const gem = (x: number, y: number, color: number, s: number, emi = 0): void => {
    b.part(b.g.sph, b.mk('dark', { color, flat: true, ...(emi ? { emissive: color, emi } : {}) }), { x, y, z: z + 0.03, sx: s, sy: s, sz: s * 0.85, noShell: true }, m);
  };
  // a 3D spike (horn / ear / ray / plume / fang): a cone with real depth (sz=w)
  // angled OUT of the face plane (rz spreads, rx tips toward/away the camera).
  const spike = (
    x: number, y: number, w: number, h: number, rz: number, color: number,
    o: { z?: number; rx?: number; emissive?: number; emi?: number; flat?: boolean } = {},
  ): void => {
    b.part(
      b.g.cone,
      b.mk('dark', { color, flat: o.flat ?? true, ...(o.emissive !== undefined ? { emissive: o.emissive, emi: o.emi } : {}) }),
      { x, y, z: (o.z ?? z) - 0.02, sx: w, sy: h, sz: w, rz, rx: o.rx ?? 0 },
      m,
    );
  };

  switch (idx) {
    case 0: // domino — dark curved eye-band dome + white eyes + accent ribbon
      cap(0x241c30, 0.4 * sc, 0.17 * sc);
      whiteEyes();
      // ribbon tie — two small 3D wedges flaring off the right edge
      spike(0.27 * sc, ey + 0.03 * sc, 0.06 * sc, 0.11 * sc, -1.1, acc, { rx: -0.4 });
      spike(0.28 * sc, ey - 0.05 * sc, 0.06 * sc, 0.11 * sc, -2.1, acc, { rx: -0.4 });
      break;
    case 1: // feline — dark curved cat dome + 3D ears + whiskers + accent eyes
      cap(0x2a1626, 0.38 * sc, 0.17 * sc);
      for (const sgn of [-1, 1]) {
        spike(sgn * 0.2 * sc, ey + 0.21 * sc, 0.12 * sc, 0.22 * sc, sgn * 0.28, 0x2a1626, { rx: -0.45, z: z - 0.05 });
        spike(sgn * 0.2 * sc, ey + 0.19 * sc, 0.06 * sc, 0.12 * sc, sgn * 0.28, acc, { rx: -0.45, z: z - 0.02 }); // inner ear
      }
      glowEyes(acc, 0.05);
      // whiskers — thin slats; MUST be noShell (a hairline box's inverted-hull
      // outline inflates by rimWidth/minDim ≈ 4.7×, ballooning into giant bars).
      for (const sgn of [-1, 1])
        for (const dy of [0.0, -0.035, -0.07])
          b.part(b.g.box, b.mk('dark', { color: 0x2a1828 }), { x: sgn * 0.24 * sc, y: ey + (dy - 0.04) * sc, z: z + 0.03, sx: 0.16 * sc, sy: 0.014, sz: 0.02, rz: sgn * 0.1, noShell: true }, m);
      break;
    case 2: { // harlequin — crimson curved dome + gold gem diamonds + bell tips
      cap(0xb83a52, 0.4 * sc, 0.17 * sc);
      whiteEyes();
      for (const [dx, dyy] of [[-0.17, 0.05], [-0.06, -0.02], [0.06, -0.02], [0.17, 0.05], [0, 0.08]] as [number, number][])
        gem(dx * sc, ey + dyy * sc, 0xf1d780, 0.045 * sc, 0.25);
      for (const sgn of [-1, 1]) gem(sgn * 0.25 * sc, ey + 0.11 * sc, 0xf1d780, 0.04 * sc, 0.35);
      break;
    }
    case 3: { // phantom (EPIC) — porcelain HALF mask + gold seam, gilded cheek
      //        filigree + a glinting brow gem (subtle pulse). Opera elegance.
      cap(0xf2ead6, 0.24 * sc, 0.27 * sc, { x: -0.11 * sc, depth: 0.22 });
      b.part(b.g.sph, b.basic(0x09060d), { x: -0.1 * sc, y: ey + 0.01 * sc, z: z + 0.02, sx: 0.07 * sc, sy: 0.09 * sc, sz: 0.06, noShell: true, shadow: false }, m); // socket
      b.part(b.g.box, b.mk('dark', { color: 0xe8c870 }), { x: 0.0, y: ey, z: z + 0.03, sx: 0.03 * sc, sy: 0.3 * sc, sz: 0.05, noShell: true }, m); // gold seam
      const gemMat = b.mk('dark', { color: 0xff6f91, emissive: 0xff6f91, emi: 0.8, flat: true });
      b.part(b.g.sph, gemMat, { x: -0.11 * sc, y: ey + 0.14 * sc, z: z + 0.03, s: 0.03 * sc, noShell: true }, m); // brow gem
      for (const [i, dy] of [0.0, -0.06, -0.11].entries())
        b.part(b.g.sph, b.mk('dark', { color: 0xe8c870, flat: true }), { x: (-0.16 - i * 0.01) * sc, y: ey + dy * sc, z: z + 0.02, s: 0.016 * sc, noShell: true }, m); // cheek filigree
      addEye(b, m, 0.1 * sc, ey, eyeZ, 0.06 * sc, 0.06 * sc); // uncovered eye
      b.addFx?.((_dt, t) => { gemMat.emissiveIntensity = 0.6 + 0.4 * (0.5 + 0.5 * Math.sin(t * 2.4)); });
      break;
    }
    case 4: { // oni (EPIC) — crimson dome + bone horns with GILDED rings + gold
      //        brows + a glinting forehead gem + fangs + ember eyes. Regal demon.
      cap(0xc8281c, 0.4 * sc, 0.19 * sc);
      for (const sgn of [-1, 1])
        spike(sgn * 0.2 * sc, ey + 0.24 * sc, 0.09 * sc, 0.28 * sc, sgn * 0.5, 0xe8d6a0, { rx: -0.35, z: z - 0.05 });
      for (const sgn of [-1, 1]) // gilded horn ring
        b.part(b.g.cyl, b.mk('dark', { color: 0xf1d780, flat: true }), { x: sgn * 0.2 * sc, y: ey + 0.2 * sc, z: z - 0.05, sx: 0.075 * sc, sy: 0.026 * sc, sz: 0.075 * sc }, m);
      for (const sgn of [-1, 1])
        b.part(b.g.box, b.mk('dark', { color: 0xf1d780 }), { x: sgn * 0.1 * sc, y: ey + 0.09 * sc, z: z + 0.02, sx: 0.14 * sc, sy: 0.03 * sc, sz: 0.05, rz: sgn * 0.45, noShell: true }, m); // brow
      const gemMat = b.mk('dark', { color: 0xffd34a, emissive: 0xffae3a, emi: 0.9, flat: true });
      b.part(b.g.sph, gemMat, { x: 0, y: ey + 0.085 * sc, z: z + 0.03, s: 0.035 * sc, noShell: true }, m); // forehead gem
      glowEyes(0xffae4a);
      for (const sgn of [-1, 1]) // fangs
        spike(sgn * 0.06 * sc, ey - 0.14 * sc, 0.035 * sc, 0.085 * sc, 0, 0xfff0e0, { rx: Math.PI, z: z + 0.0 });
      b.addFx?.((_dt, t) => { gemMat.emissiveIntensity = 0.6 + 0.45 * (0.5 + 0.5 * Math.sin(t * 2.5)); });
      break;
    }
    case 5: { // sunburst (EPIC) — domed gold sun face + TWO-TIER radiating rays
      //        (long + short) + a gem sun-core + amber eyes. Rays shimmer.
      cap(0xf0a838, 0.22 * sc, 0.22 * sc, { emissive: 0xf0a838, emi: 0.25, depth: 0.22 });
      const rayMat = b.mk('dark', { color: 0xffd770, flat: true, emissive: 0xffd770, emi: 0.2 });
      const n = 12;
      for (let i = 0; i < n; i++) {
        const ang = (i / n) * Math.PI * 2;
        const long = i % 2 === 0;
        const rad = long ? 0.28 : 0.23;
        b.part(b.g.cone, rayMat, { x: Math.sin(ang) * rad * sc, y: ey + Math.cos(ang) * rad * sc, z: z - 0.04, sx: (long ? 0.046 : 0.034) * sc, sy: (long ? 0.16 : 0.1) * sc, sz: (long ? 0.046 : 0.034) * sc, rz: -ang }, m);
      }
      const coreMat = b.mk('dark', { color: 0xfff0c0, emissive: 0xffc24a, emi: 0.9, flat: true });
      b.part(b.g.sph, coreMat, { x: 0, y: ey, z: z + 0.03, s: 0.07 * sc, noShell: true }, m); // sun-core gem
      glowEyes(0xffc24a, 0.045);
      b.addFx?.((_dt, t) => {
        rayMat.emissiveIntensity = 0.14 + 0.18 * (0.5 + 0.5 * Math.sin(t * 2.2));
        coreMat.emissiveIntensity = 0.7 + 0.4 * (0.5 + 0.5 * Math.sin(t * 2.2));
      });
      break;
    }
    case 6: { // void (MYTHIC) — a window into deep space: dark curved orb, ENLARGED
      //        and rimmed with a violet emissive edge so the silhouette pops off
      //        the face, holding a BRIGHT saturated twin-nebula (additive) + a
      //        denser starfield with two bright haloed stars + pulsing violet
      //        eyes. The rotating cosmos is the signature mythic flourish.
      cap(0x0a0618, 0.4 * sc, 0.25 * sc, { depth: 0.26 });
      // thin violet emissive rim around the mask edge — the cosmic window's cut
      const rimGeo = new THREE.TorusGeometry(0.2, 0.02, 8, 28);
      b.own(rimGeo);
      b.part(rimGeo, b.mk('dark', { color: 0x9a6ad8, emissive: 0x9a6ad8, emi: 0.8, flat: false }), { y: ey, z: z + 0.005, sx: sc * 1.02, sy: sc * 0.68, sz: 1, noShell: true }, m);
      const sky = new THREE.Group();
      sky.position.set(0, ey, z + 0.01);
      m.add(sky);
      const neb1 = b.basic(0xe060d0, { transparent: true, opacity: 0.65 });
      neb1.blending = THREE.AdditiveBlending;
      neb1.depthWrite = false;
      neb1.toneMapped = false;
      const neb2 = b.basic(0x5a80ff, { transparent: true, opacity: 0.5 });
      neb2.blending = THREE.AdditiveBlending;
      neb2.depthWrite = false;
      neb2.toneMapped = false;
      b.part(b.g.sph, neb1, { x: -0.04 * sc, y: 0.02 * sc, z: 0, sx: 0.2 * sc, sy: 0.13 * sc, sz: 0.03, noShell: true, shadow: false }, sky);
      b.part(b.g.sph, neb2, { x: 0.06 * sc, y: -0.02 * sc, z: 0, sx: 0.16 * sc, sy: 0.11 * sc, sz: 0.03, noShell: true, shadow: false }, sky);
      for (const [dx, dyy, ss] of [[-0.12, 0.05, 0.016], [0.1, 0.06, 0.013], [0.05, -0.03, 0.018], [-0.06, -0.05, 0.012], [0.15, -0.01, 0.013], [0, 0.1, 0.014], [-0.14, -0.02, 0.011], [0.13, -0.08, 0.012], [-0.02, -0.09, 0.013]] as [number, number, number][])
        b.part(b.g.sph, b.basic(0xe8dcff), { x: dx * sc, y: dyy * sc, z: 0.01, s: ss * sc, noShell: true, shadow: false }, sky);
      // two BRIGHT stars with additive halos so the cosmos reads as lit, not flat
      for (const [dx, dyy] of [[-0.09, 0.08], [0.11, -0.05]] as [number, number][]) {
        b.part(b.g.sph, b.basic(0xfff6ff), { x: dx * sc, y: dyy * sc, z: 0.015, s: 0.026 * sc, noShell: true, shadow: false }, sky);
        const haloMat = b.basic(0xd8c0ff, { transparent: true, opacity: 0.5 });
        haloMat.blending = THREE.AdditiveBlending;
        haloMat.depthWrite = false;
        haloMat.toneMapped = false;
        b.part(b.g.sph, haloMat, { x: dx * sc, y: dyy * sc, z: 0.012, s: 0.06 * sc, noShell: true, shadow: false }, sky);
      }
      sockets(0.05);
      const eyeMat = b.mk('dark', { color: 0x9a6ad8, emissive: 0x9a6ad8, emi: 0.9, flat: false });
      for (const sgn of [-1, 1])
        b.part(b.g.sph, eyeMat, { x: sgn * 0.1 * sc, y: ey, z: eyeZ, sx: 0.05 * sc, sy: 0.052 * sc, sz: 0.042 * sc, noShell: true, shadow: false }, m);
      b.addFx?.((_dt, t) => {
        sky.rotation.z = t * 0.25;
        eyeMat.emissiveIntensity = 0.7 + 0.5 * Math.sin(t * 2.8);
        neb1.opacity = 0.55 + 0.18 * (0.5 + 0.5 * Math.sin(t * 1.5));
      });
      break;
    }
    case 7: { // crimson-veil (MYTHIC) — domed red half-mask + gold brow + pulsing
      //        ember eyes + a draped 3D veil that SWAYS like fabric, ember beads
      //        glowing at the fold tips. Animated regal mythic.
      cap(0xd83440, 0.34 * sc, 0.16 * sc, { emissive: 0x4a0808, emi: 0.16 });
      b.part(b.g.box, b.mk('dark', { color: 0xe8c870 }), { y: ey + 0.09 * sc, z: z + 0.02, sx: 0.34 * sc, sy: 0.025 * sc, sz: 0.05, noShell: true }, m); // gold brow trim
      sockets(0.045);
      const eyeMat = b.mk('dark', { color: 0xff9a40, emissive: 0xff7a2a, emi: 1.0, flat: false });
      for (const sgn of [-1, 1])
        b.part(b.g.sph, eyeMat, { x: sgn * 0.1 * sc, y: ey, z: eyeZ, sx: 0.045 * sc, sy: 0.047 * sc, sz: 0.038 * sc, noShell: true, shadow: false }, m);
      // swaying draped veil — folds hang under one group that rocks gently
      const veil = new THREE.Group();
      veil.position.set(0, ey - 0.04 * sc, z - 0.05);
      m.add(veil);
      const beadMat = b.mk('dark', { color: 0xffae4a, emissive: 0xffae4a, emi: 0.9, flat: true });
      for (const [dx, w] of [[-0.12, 0.07], [0, 0.085], [0.12, 0.07]] as [number, number][]) {
        b.part(b.g.cone, b.mk('dark', { color: 0xc02a32, flat: false, emissive: 0x360606, emi: 0.12 }), { x: dx * sc, y: -0.13 * sc, z: 0, sx: w * sc, sy: 0.24 * sc, sz: 0.1, rx: Math.PI }, veil);
        b.part(b.g.sph, beadMat, { x: dx * sc, y: -0.23 * sc, z: 0, s: 0.022 * sc, noShell: true }, veil);
      }
      b.addFx?.((_dt, t) => {
        veil.rotation.z = Math.sin(t * 1.4) * 0.05;
        eyeMat.emissiveIntensity = 0.8 + 0.5 * Math.sin(t * 3);
        beadMat.emissiveIntensity = 0.7 + 0.45 * Math.sin(t * 3 + 1);
      });
      break;
    }
    case 8: { // mardi-gras — purple curved dome + 3D feather plumes + gold studs + gems
      cap(0x5e2f8e, 0.36 * sc, 0.17 * sc);
      const plumeCols = [0xa06ed0, 0x7a4ea8, 0xc8a0e0];
      for (const sgn of [-1, 1])
        for (const [i, ang] of [0.25, 0.55, 0.85].entries())
          spike(sgn * (0.15 + i * 0.05) * sc, ey + 0.21 * sc, 0.05 * sc, 0.24 * sc, sgn * ang, plumeCols[i] ?? 0xa06ed0, { rx: -0.5, z: z - 0.06 });
      for (const [dx, dyy] of [[-0.14, 0.02], [0.14, 0.02], [0, 0.06]] as [number, number][])
        gem(dx * sc, ey + dyy * sc, 0xf1d780, 0.024 * sc, 0.35);
      gem(-0.1 * sc, ey, 0xb07ee0, 0.05 * sc);
      gem(0.1 * sc, ey, 0x5fa85e, 0.05 * sc);
      break;
    }
    case 9: { // mirror (MYTHIC) — owner, verbatim: "i dont like how mirror
      //        mask looks, why is there a bunch of mirrors on the mask,
      //        should be clean." The 6-facet "cut glass" plate cluster is
      //        GONE. New design: ONE clean polished convex mirror (cool pale
      //        silver, subtle emissive so the dome itself shades smoothly —
      //        no facet seams), a single elegant diagonal glare streak, and
      //        a thin gold rim with no corner studs. Elegance over busyness.
      cap(0xdde6f2, 0.34 * sc, 0.24 * sc, { emissive: 0xdde6f2, emi: 0.3, depth: 0.2 });
      // thin gold rim — one slim torus, no corner gems
      const rim = new THREE.TorusGeometry(0.2, 0.018, 8, 32);
      b.own(rim);
      b.part(rim, b.mk('dark', { color: 0xf0d488, emissive: 0x8a6c1e, emi: 0.35 }), { y: ey, z: z + 0.01, sx: sc * 1.0, sy: sc * 0.62, sz: 1, noShell: true }, m);
      // ONE elegant diagonal glare band, visible at construction — its slow
      // sweep rides on top via addFx.
      const glareMat = b.basic(0xffffff, { transparent: true, opacity: 0.45 });
      glareMat.blending = THREE.AdditiveBlending;
      glareMat.depthWrite = false;
      glareMat.toneMapped = false;
      const glare = b.part(b.g.box, glareMat, { x: -0.04 * sc, y: ey, z: z + 0.07, sx: 0.05 * sc, sy: 0.3 * sc, sz: 0.01, rz: 0.45, noShell: true, shadow: false }, m);
      whiteEyes();
      b.addFx?.((_dt, t) => {
        glare.position.x = Math.sin(t * 0.7) * 0.1 * sc; // slow sweep
        glareMat.opacity = 0.35 + 0.25 * Math.abs(Math.cos(t * 0.7));
      });
      break;
    }
  }
}

// ───────────────────────── outfits (body clothing / armor) ─────────────────────────

/**
 * Outfit ids — clothing / armor layered onto the humanoid FIGURE's BODY
 * (torso / arms / legs). NOT new body shapes: the head still carries the
 * icon's body-shape silhouette + face + hat + mask; this dresses the
 * consistent brawler body underneath. MIRRORS the 2D OUTFITS registry order
 * in web/.../characterParts.tsx and the `outfit-${id}` catalog ids.
 * `icon.outfit` stores the BARE id (e.g. 'plate'), resolved the same way
 * masks resolve. Unknown / undefined / '' → bare body (NO regression).
 */
export const OUTFIT_IDS = ['tunic', 'jerkin', 'cloak', 'robe', 'plate', 'regalia'] as const;
export const OUTFIT_COUNT = OUTFIT_IDS.length; // 6

/**
 * Context the host (figures.ts) hands {@link buildOutfit}. Like {@link
 * CharPartCtx} but anchored to the BODY: outfit pieces parent to `torso` (the
 * animated core — bob / lean / squash) or to an arm / leg PIVOT so sleeves /
 * bracers / pauldrons / greaves SWING with the gait. Reuses the host's
 * material / outline / disposal system so outfit parts get toon outline shells
 * + shadows + cleanup for free.
 */
export interface OutfitCtx {
  g: GeoSet;
  mk: (role: Role, o?: MkOpts) => THREE.MeshToonMaterial;
  /** Add a mesh; pass an explicit `parent` (torso / arm / leg) for outfits. */
  part: (geom: THREE.BufferGeometry, mat: THREE.Material, o: PartOpts, parent?: THREE.Object3D) => THREE.Mesh;
  basic: (color: number, o?: { transparent?: boolean; opacity?: number }) => THREE.MeshBasicMaterial;
  own: (geom: THREE.BufferGeometry) => void;
  /** Register a per-frame updater (mythic ember pulse / drifting motes). */
  addFx?: (fn: (dt: number, elapsed: number) => void) => void;
  colors: HeadColors;
  /** The animated core — torso pieces (chest / skirt / cape / collar) attach here. */
  torso: THREE.Object3D;
  /** [left(-x), right(+x)] arm pivots — sleeves / pauldrons / bracers attach here. */
  arms: readonly [THREE.Object3D, THREE.Object3D];
  /** [left(-x), right(+x)] leg pivots — greaves attach here. */
  legs: readonly [THREE.Object3D, THREE.Object3D];
}

/**
 * Dress the humanoid figure in an OUTFIT (clothing / armor). `outfit` is a
 * lobby outfit id string (e.g. `'plate'`) or a raw index (catalog renderer);
 * none / unknown → renders nothing (bare body — the no-regression default).
 *
 * Pieces attach to the body anchors in {@link OutfitCtx} so they move with the
 * walk / hop gait: chest / skirt / cape / collar on the `torso` (core), sleeves
 * / pauldrons / bracers on the arm pivots, greaves on the leg pivots. Built so
 * NOTHING reaches above the neck (~y 1.15) at the front, so the head / hat /
 * mask never clip. Palette-following cloth (tunic / cloak / robe) uses a deeper
 * shade of the body color; themed armor (jerkin leather, plate steel, regalia
 * gold) uses fixed material colors; ACCENT is the trim throughout. High-rarity
 * sets carry rarity-worthy detail (gilt + emissive + an animated ember
 * flourish for the mythic regalia via {@link OutfitCtx.addFx}).
 */
export function buildOutfit(b: OutfitCtx, outfit: number | string | undefined): void {
  const idx = resolveCosmetic(outfit, OUTFIT_IDS, OUTFIT_COUNT);
  if (idx < 0) return; // none / unknown → bare body (no regression)
  const g = b.g;
  const primary = b.colors.primary;
  const accent = b.colors.accent;
  const torso = b.torso;
  const [armL, armR] = b.arms;
  const [legL, legR] = b.legs;

  switch (idx) {
    case 0: { // tunic (rarity 1, default) — belted overtunic in a deeper shade
      //          of the body color + accent V-collar / belt / sleeve cuffs.
      const base = shadeHex(primary, 0.18);
      b.part(g.box, b.mk('primary', { color: base }), { y: 0.86, sx: 0.54, sy: 0.52, sz: 0.38 }, torso); // chest
      b.part(g.box, b.mk('primary', { color: base }), { y: 0.46, sx: 0.52, sy: 0.3, sz: 0.38 }, torso);  // skirt hem
      b.part(g.box, b.mk('accent'), { y: 0.6, sx: 0.58, sy: 0.09, sz: 0.4 }, torso);                     // belt
      b.part(g.box, b.mk('dark', { color: PALETTE.gold, emissive: PALETTE.gold, emi: 0.2 }), { y: 0.6, z: 0.21, sx: 0.1, sy: 0.1, sz: 0.04, noShell: true }, torso); // buckle
      for (const sgn of [-1, 1]) // V-collar trim
        b.part(g.box, b.mk('accent'), { x: sgn * 0.07, y: 1.0, z: 0.18, sx: 0.18, sy: 0.12, sz: 0.04, rz: sgn * 0.6, noShell: true }, torso);
      for (const arm of [armL, armR]) // rolled sleeve cuffs
        b.part(g.cyl, b.mk('primary', { color: base }), { y: -0.16, sx: 0.18, sy: 0.26, sz: 0.18 }, arm);
      break;
    }
    case 1: { // jerkin (rarity 2) — leather vest + shoulder straps + laced front
      //          + bracers. Themed leather (not palette); accent laces / buckle.
      const leather = 0x6e4a2c;
      const leatherDk = 0x4e3320;
      b.part(g.box, b.mk('dark', { color: leather }), { y: 0.88, sx: 0.52, sy: 0.5, sz: 0.38 }, torso); // vest
      for (const sgn of [-1, 1]) // shoulder straps
        b.part(g.box, b.mk('dark', { color: leatherDk }), { x: sgn * 0.16, y: 1.04, sx: 0.12, sy: 0.16, sz: 0.42 }, torso);
      b.part(g.box, b.mk('dark', { color: leatherDk }), { y: 0.9, z: 0.19, sx: 0.12, sy: 0.4, sz: 0.04, noShell: true }, torso); // lacing panel
      for (const dy of [0.12, 0.0, -0.12]) // cross laces
        b.part(g.box, b.mk('accent'), { y: 0.9 + dy, z: 0.21, sx: 0.14, sy: 0.02, sz: 0.03, rz: 0.5, noShell: true }, torso);
      b.part(g.box, b.mk('dark', { color: leatherDk }), { y: 0.6, sx: 0.56, sy: 0.1, sz: 0.4 }, torso); // belt
      b.part(g.box, b.mk('dark', { color: PALETTE.gold, emissive: PALETTE.gold, emi: 0.2 }), { y: 0.6, z: 0.21, sx: 0.1, sy: 0.1, sz: 0.04, noShell: true }, torso); // buckle
      for (const arm of [armL, armR]) // bracers
        b.part(g.cyl, b.mk('dark', { color: leather }), { y: -0.34, sx: 0.17, sy: 0.18, sz: 0.17 }, arm);
      break;
    }
    case 2: { // cloak (rarity 3) — shoulder drape + long BACK cape + neck hood
      //          (behind the head, never over it) + gold throat clasp. Deep
      //          shade of the body color, accent edge trim.
      const base = shadeHex(primary, 0.3);
      b.part(g.sphLP, b.mk('primary', { color: base }), { y: 0.98, z: -0.06, sx: 0.62, sy: 0.46, sz: 0.46 }, torso); // shoulder drape
      b.part(g.box, b.mk('primary', { color: base }), { y: 0.5, z: -0.2, sx: 0.5, sy: 0.86, sz: 0.1 }, torso);        // back cape
      b.part(g.cone, b.mk('primary', { color: base }), { y: 0.12, z: -0.18, sx: 0.4, sy: 0.22, sz: 0.16, rx: Math.PI }, torso); // hem flare
      b.part(g.box, b.mk('accent'), { y: 0.5, z: -0.25, sx: 0.5, sy: 0.86, sz: 0.02, noShell: true }, torso);          // edge trim
      b.part(g.sph, b.mk('primary', { color: base }), { y: 1.14, z: -0.18, sx: 0.36, sy: 0.32, sz: 0.32 }, torso);    // neck hood (behind head)
      b.part(g.sph, b.mk('dark', { color: PALETTE.gold, emissive: PALETTE.gold, emi: 0.3 }), { y: 1.02, z: 0.16, s: 0.08, noShell: true }, torso); // clasp
      break;
    }
    case 3: { // robe (rarity 3) — full-length mage robe + wide draping sleeves
      //          + gold front placket / sash / cuffs + a glowing collar gem.
      const base = shadeHex(primary, 0.24);
      b.part(g.cyl, b.mk('primary', { color: base }), { y: 0.56, sx: 0.48, sy: 1.0, sz: 0.42 }, torso); // robe body
      b.part(g.cone, b.mk('primary', { color: base }), { y: 0.16, sx: 0.56, sy: 0.3, sz: 0.5, rx: Math.PI }, torso); // hem flare
      b.part(g.box, b.mk('accent'), { y: 0.55, z: 0.22, sx: 0.07, sy: 0.92, sz: 0.03, noShell: true }, torso); // gold placket
      b.part(g.box, b.mk('accent'), { y: 0.64, sx: 0.52, sy: 0.1, sz: 0.46 }, torso); // sash
      for (const sgn of [-1, 1]) // V collar
        b.part(g.box, b.mk('accent'), { x: sgn * 0.08, y: 1.02, z: 0.19, sx: 0.2, sy: 0.12, sz: 0.04, rz: sgn * 0.6, noShell: true }, torso);
      b.part(g.sph, b.mk('accent', { emissive: accent, emi: 0.7, flat: false }), { y: 0.92, z: 0.22, s: 0.07, noShell: true }, torso); // collar gem
      for (const arm of [armL, armR]) { // wide draping sleeves + gold cuff
        b.part(g.cyl, b.mk('primary', { color: base }), { y: -0.22, sx: 0.22, sy: 0.5, sz: 0.22 }, arm);
        b.part(g.cyl, b.mk('accent'), { y: -0.44, sx: 0.2, sy: 0.08, sz: 0.2 }, arm);
      }
      break;
    }
    case 4: { // plate (rarity 4) — POLISHED steel plate: chestplate + ridge +
      //          emblem gem, gorget, faulds, GOLD TRIM edges + emissive
      //          rivets, rounded pauldrons + bracers + greaves. Brightened
      //          from a muddy grey to an actual polished-steel-and-gold look
      //          — the trim is FIXED gold (not player accent) so it reads as
      //          gold armor on every wearer, matching the rarity's price.
      const steel = 0xb8c4d4;
      const steelDk = 0x7a8494;
      const gold = PALETTE.gold;
      b.part(g.box, b.mk('dark', { color: steel, flat: true }), { y: 0.9, sx: 0.56, sy: 0.5, sz: 0.42 }, torso); // chestplate
      b.part(g.box, b.mk('dark', { color: gold, emissive: gold, emi: 0.3 }), { y: 0.9, z: 0.22, sx: 0.06, sy: 0.46, sz: 0.06, noShell: true }, torso); // gold ridge trim
      b.part(g.sph, b.mk('accent', { emissive: accent, emi: 0.5, flat: false }), { y: 0.98, z: 0.23, s: 0.08, noShell: true }, torso); // emblem gem
      b.part(g.cyl8, b.mk('dark', { color: steelDk, flat: true }), { y: 1.08, sx: 0.34, sy: 0.12, sz: 0.3 }, torso); // gorget
      b.part(g.box, b.mk('dark', { color: steel, flat: true }), { y: 0.54, sx: 0.54, sy: 0.18, sz: 0.42 }, torso); // fauld 1
      b.part(g.box, b.mk('dark', { color: steelDk, flat: true }), { y: 0.4, sx: 0.46, sy: 0.16, sz: 0.4 }, torso); // fauld 2
      for (const sx of [-0.2, 0, 0.2]) // gold emissive rivets
        b.part(g.sph, b.mk('dark', { color: gold, emissive: gold, emi: 0.55, flat: true }), { x: sx, y: 1.1, z: 0.16, s: 0.035, noShell: true }, torso);
      for (const arm of [armL, armR]) { // pauldron + gold trim band + bracer
        b.part(g.sphLP, b.mk('dark', { color: steel, flat: true }), { y: 0.0, sx: 0.26, sy: 0.22, sz: 0.26 }, arm);
        b.part(g.cyl8, b.mk('dark', { color: gold, emissive: gold, emi: 0.25 }), { y: 0.04, sx: 0.27, sy: 0.05, sz: 0.27, noShell: true }, arm);
        b.part(g.cyl, b.mk('dark', { color: steelDk, flat: true }), { y: -0.34, sx: 0.18, sy: 0.2, sz: 0.18 }, arm);
      }
      for (const leg of [legL, legR]) // greaves
        b.part(g.cyl, b.mk('dark', { color: steel, flat: true }), { y: -0.3, sx: 0.22, sy: 0.36, sz: 0.22 }, leg);
      break;
    }
    case 5: { // regalia (rarity 5, MYTHIC) — "Emberforged Regalia": gilded plate
      //          w/ a glowing ember core, spiked gold pauldrons w/ ember gems, a
      //          regal crimson cape w/ gold trim, gilded bracers + greaves, and
      //          rising ember motes. Animated flourish (pulse + drift) via addFx.
      const gold = 0xe8c24a;
      const goldDk = 0x9a7a22;
      const crimson = 0x7a1622;
      b.part(g.box, b.mk('dark', { color: gold, emissive: 0x5a3a08, emi: 0.2 }), { y: 0.9, sx: 0.56, sy: 0.5, sz: 0.42 }, torso); // chestplate
      b.part(g.box, b.mk('dark', { color: goldDk }), { y: 0.9, z: 0.22, sx: 0.07, sy: 0.46, sz: 0.06, noShell: true }, torso); // ridge
      const coreMat = b.mk('dark', { color: PALETTE.emberHigh, emissive: PALETTE.emberMid, emi: 1.4, flat: false });
      b.part(g.sph, coreMat, { y: 0.96, z: 0.24, s: 0.1, noShell: true }, torso); // ember core
      b.part(g.cyl8, b.mk('dark', { color: goldDk }), { y: 1.09, sx: 0.34, sy: 0.14, sz: 0.3 }, torso); // high collar
      b.part(g.box, b.mk('dark', { color: gold, emissive: 0x4a2e06, emi: 0.15 }), { y: 0.52, sx: 0.54, sy: 0.2, sz: 0.42 }, torso); // faulds
      b.part(g.box, b.mk('dark', { color: crimson }), { y: 0.48, z: -0.22, sx: 0.52, sy: 0.9, sz: 0.08 }, torso); // crimson cape
      b.part(g.box, b.mk('dark', { color: gold, emissive: 0x4a2e06, emi: 0.15 }), { y: 0.48, z: -0.27, sx: 0.52, sy: 0.9, sz: 0.02, noShell: true }, torso); // cape gold trim
      b.part(g.cone, b.mk('dark', { color: crimson }), { y: 0.1, z: -0.2, sx: 0.42, sy: 0.24, sz: 0.14, rx: Math.PI }, torso); // cape hem
      const gemMat = b.mk('dark', { color: PALETTE.emberHigh, emissive: PALETTE.emberMid, emi: 1.2, flat: false });
      for (const [i, arm] of [armL, armR].entries()) {
        const sgn = i === 0 ? -1 : 1;
        b.part(g.sphLP, b.mk('dark', { color: gold, emissive: 0x4a2e06, emi: 0.15 }), { y: 0.02, sx: 0.28, sy: 0.24, sz: 0.28 }, arm); // pauldron
        b.part(g.cone, b.mk('dark', { color: goldDk }), { x: sgn * 0.08, y: 0.18, sx: 0.1, sy: 0.26, sz: 0.1, rz: sgn * 0.4 }, arm); // spike
        b.part(g.sph, gemMat, { y: 0.04, z: 0.18, s: 0.05, noShell: true }, arm); // pauldron gem
        b.part(g.cyl, b.mk('dark', { color: gold }), { y: -0.34, sx: 0.18, sy: 0.2, sz: 0.18 }, arm); // gilded bracer
      }
      for (const leg of [legL, legR]) // gilded greaves
        b.part(g.cyl, b.mk('dark', { color: gold, emissive: 0x4a2e06, emi: 0.12 }), { y: -0.3, sx: 0.22, sy: 0.36, sz: 0.22 }, leg);
      const motes = [-0.2, 0.0, 0.2, 0.1].map((sx, i) => {
        const mat = b.basic(PALETTE.emberHigh, { transparent: true, opacity: 0.85 });
        const mesh = b.part(g.sph, mat, { x: sx, y: 0.95, z: 0.1, s: 0.03, noShell: true, shadow: false }, torso);
        return { mesh, mat, sx, phase: i / 4 };
      });
      b.addFx?.((_dt, t) => {
        coreMat.emissiveIntensity = 1.1 + 0.5 * Math.sin(t * 3.0);
        gemMat.emissiveIntensity = 1.0 + 0.4 * Math.sin(t * 3.0 + 1.0);
        for (const m of motes) {
          const p = (t * 0.45 + m.phase) % 1; // rising loop
          m.mesh.position.y = 0.92 + p * 0.5;
          m.mesh.position.x = m.sx + Math.sin(t * 2 + m.phase * 6) * 0.03;
          m.mat.opacity = 0.85 * (1 - p);
        }
      });
      break;
    }
  }
}

// ───────────────────────── border auras (whole-figure rings) ─────────────────────────

/** A built border aura: its group (under the figure root) + an optional
 *  per-frame updater for animated borders (gentle spin / glow pulse). */
export interface BorderAura {
  group: THREE.Group;
  tick?: (dt: number, elapsed: number) => void;
}

/**
 * Build a tasteful in-game AURA for a lobby BORDER cosmetic. A 2D avatar FRAME
 * has no literal 3D form, so each border becomes a subtle glowing GROUND RING
 * around the character's feet + a few themed rim accents (leaves, rivets,
 * candle flames, crystal shards, plumes, rose buds …) in the border's color.
 * Mirrors the 2D BORDERS order (0 leaf-ring … 11 gilded-rose-ring). Unknown /
 * none → null (renders nothing). Kept low + subtle so it frames the player
 * without cluttering fast top-down gameplay.
 *
 * Attaches under `opts.parent` (the figure ROOT, so it rings the feet on the
 * ground and does not bob with the body). Animated borders return a `tick`.
 */
export function buildBorderAura(
  b: CharPartCtx,
  border: number | string | undefined,
  opts: { parent: THREE.Object3D; radius?: number },
): BorderAura | null {
  const idx = resolveCosmetic(aliasBorderId(border), BORDER_IDS, BORDER_COUNT);
  if (idx < 0) return null;
  const R = opts.radius ?? 0.9;
  const aura = new THREE.Group();
  opts.parent.add(aura);
  const pulseMats: { opacity: number }[] = [];
  // Bespoke per-frame motion for high-rarity auras (rising embers, twinkling
  // stars) that the generic spin/pulse can't express. Each gets (dt, elapsed).
  const customTicks: ((dt: number, elapsed: number) => void)[] = [];
  let spin = 0;

  // A border RING band. Prominence pass: every band now sits over a soft
  // ADDITIVE under-glow disc (so the ring reads as a LIT base plate that frames
  // the feet, not a thin translucent hairline), the band is WIDER (inner R*0.78)
  // and brighter (opacity floor lifted ~1.4×). Kept low on the ground + additive
  // so it pops against any floor without washing out the character above it.
  const ring = (color: number, op = 0.5, inner = R * 0.78, outer = R): THREE.MeshBasicMaterial => {
    // soft additive halo under the band — the "lit base" read
    const gg = new THREE.CircleGeometry(outer * 1.08, 44);
    b.own(gg);
    const glowMat = b.basic(color, { transparent: true, opacity: Math.min(0.55, op * 0.7) });
    glowMat.blending = THREE.AdditiveBlending;
    glowMat.depthWrite = false;
    glowMat.toneMapped = false;
    const glow = new THREE.Mesh(gg, glowMat);
    glow.rotation.x = -Math.PI / 2;
    glow.position.y = 0.028;
    glow.renderOrder = -2;
    aura.add(glow);
    // the crisp ring band itself (wider + brighter)
    const g = new THREE.RingGeometry(inner, outer, 56);
    b.own(g);
    const mat = b.basic(color, { transparent: true, opacity: Math.min(0.95, op * 1.4) });
    const mesh = new THREE.Mesh(g, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = 0.04;
    mesh.renderOrder = -1;
    aura.add(mesh);
    return mat;
  };
  const rimRing = (count: number, build: (x: number, z: number, a: number) => void): void => {
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      build(Math.sin(a) * R, Math.cos(a) * R, a);
    }
  };
  // Prominence: a bright emissive "beacon" node dropped at each rim position so
  // even the plainer borders (leaf / brass / foam) carry a clear glowing accent
  // ring that reads from the top-down camera. Themed to the border color.
  const beacon = (x: number, z: number, color: number, s = 0.075): void => {
    b.part(b.g.sph, b.mk('dark', { color, emissive: color, emi: 1.1, flat: false }), { x, z, y: 0.09, s, noShell: true, shadow: false }, aura);
  };
  /**
   * SIGNATURE VERTICAL ELEMENT — owner: "auras are the most lacking
   * category… everything is a flat floor ring, no vertical presence."
   * Seeds `count` small groups orbiting the figure at world-Y `height`
   * (the 0.2-0.9 band) and `radiusFrac`×R, positioned CORRECTLY AT
   * CONSTRUCTION (not only inside the pushed customTick — the offscreen
   * locker/shop thumbnail baker never ticks, so a still must already show
   * the orbit position, not the origin). `build(grp, phase, i)` populates
   * one seeded group with its own child mesh(es); `speed` (rad/s) drives
   * the orbit via customTicks. Zero per-frame allocations (reuses closed-
   * over `r`/`height`/`phase` scalars, only mutates existing Vector3s).
   */
  const orbiter = (
    count: number,
    height: number,
    radiusFrac: number,
    speed: number,
    build: (grp: THREE.Group, phase: number, i: number) => void,
  ): void => {
    for (let i = 0; i < count; i++) {
      const phase = (i / count) * Math.PI * 2;
      const r = R * radiusFrac;
      const grp = new THREE.Group();
      grp.position.set(Math.sin(phase) * r, height, Math.cos(phase) * r);
      grp.rotation.y = phase;
      aura.add(grp);
      build(grp, phase, i);
      customTicks.push((_dt, t) => {
        const a = phase + t * speed;
        grp.position.set(Math.sin(a) * r, height, Math.cos(a) * r);
        grp.rotation.y = a;
      });
    }
  };

  switch (idx) {
    case 0: { // leaf-ring — glowing green ring + bigger leaf blades + bud
      //         beacons + 5 leaves SWIRLING UPWARD (vertical presence pass).
      pulseMats.push(ring(0x6aa84a, 0.6));
      rimRing(10, (x, z) => b.part(b.g.sph, b.mk('dark', { color: 0x6aa84a, flat: false }), { x, z, y: 0.08, sx: 0.14, sy: 0.05, sz: 0.24 }, aura));
      rimRing(5, (x, z) => beacon(x, z, 0x9be06a, 0.07));
      const leafMat = b.mk('dark', { color: 0x7cc45a, flat: false });
      const leaves: { mesh: THREE.Mesh; a: number; r: number; phase: number }[] = [];
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        const r = R * 0.7;
        const y = 0.2 + (i % 3) * 0.2; // staggered starting heights
        const mesh = b.part(b.g.cone, leafMat, { x: Math.sin(a) * r, z: Math.cos(a) * r, y, sx: 0.09, sy: 0.05, sz: 0.16, rx: Math.PI / 2, noShell: true, shadow: false }, aura);
        leaves.push({ mesh, a, r, phase: i / 5 });
      }
      customTicks.push((_dt, t) => {
        for (const lf of leaves) {
          const p = (t * 0.15 + lf.phase) % 1; // rising loop
          const ang = lf.a + t * 0.3;
          lf.mesh.position.set(Math.sin(ang) * lf.r, 0.2 + p * 0.7, Math.cos(ang) * lf.r);
          lf.mesh.rotation.z = t * 1.2 + lf.phase * 6;
        }
      });
      spin = 0.18;
      break;
    }
    case 1: // brass-frame — glowing brass ring + bigger rivets + gold bead
      //         beacons + 4 small brass cogs orbiting low (modest — uncommon).
      pulseMats.push(ring(0xd6a838, 0.7));
      rimRing(8, (x, z) => b.part(b.g.sph, b.mk('dark', { color: 0xf5d68a, emissive: 0xb8902a, emi: 0.5, flat: false }), { x, z, y: 0.06, s: 0.095 }, aura));
      orbiter(4, 0.22, 0.85, 0.25, (grp) => {
        b.part(b.g.cyl8, b.mk('dark', { color: 0xd6a838, flat: true }), { sx: 0.05, sy: 0.03, sz: 0.05 }, grp);
      });
      break;
    case 2: // candle-circle — ring + 8 wax candles with flames + 4 tiny
      //         floating candle flames hovering at ~0.35h.
      pulseMats.push(ring(0xffaa3d, 0.4));
      rimRing(8, (x, z) => {
        b.part(b.g.cyl, b.mk('dark', { color: 0xf0e0c0 }), { x, z, y: 0.1, sx: 0.04, sy: 0.16, sz: 0.04 }, aura);
        b.part(b.g.cone, b.mk('dark', { color: 0xffaa3d, emissive: 0xffaa3d, emi: 1.4, flat: false }), { x, z, y: 0.24, sx: 0.04, sy: 0.1, sz: 0.04, noShell: true }, aura);
      });
      orbiter(4, 0.35, 0.7, 0.2, (grp) => {
        b.part(b.g.cone, b.mk('dark', { color: 0xffaa3d, emissive: 0xffaa3d, emi: 1.3, flat: false }), { sx: 0.035, sy: 0.09, sz: 0.035, noShell: true, shadow: false }, grp);
      });
      break;
    case 3: { // gemstone-studded — warm gold band + 4 DISTINCT saturated
      //         jewel gems (ruby / emerald / sapphire / topaz), each
      //         emissive — the previous single washed accent-colored gem
      //         repeated 4x never read as "gemstone". Fixed jewel hues (not
      //         player accent) so every wearer's ring shows the same rich
      //         colors. The gems now ORBIT at ~0.4h instead of sitting on
      //         the band (the band itself is unchanged).
      pulseMats.push(ring(0xe0b840, 0.65));
      const gems = [0xe0304a, 0x2fae5a, 0x2f7ee0, 0xe0a02f]; // ruby, emerald, sapphire, topaz
      orbiter(4, 0.4, 0.75, 0.22, (grp, _phase, i) => {
        const gemColor = gems[i]!;
        b.part(b.g.sph, b.mk('dark', { color: gemColor, emissive: gemColor, emi: 1.0, flat: false }), { s: 0.1 }, grp);
      });
      break;
    }
    case 4: { // runic-glyph (EPIC) — SATURATED violet: stone ring + a bright
      //         inner glyph-circle (pulses) + a rotating outer rune-ring
      //         band (spins with the aura via `spin` below) + LARGE glowing
      //         rune monoliths on stone plinths. CALIBRATION PASS: still
      //         read washed next to Crystal Shard at 84px — the beacons
      //         were small (sx 0.04) and the outer band was a faint echo
      //         (op 0.35, same width class as the inner ring). Enlarged the
      //         rune faces, pushed their emissive higher, and made the outer
      //         band wider + much brighter so it reads as a distinct SECOND
      //         ring, not a ghost of the first.
      // 4 FLOATING glowing rune tablets orbit above the stone monoliths
      // at ~0.5h (owner: "lift them off the floor") instead of sitting on
      // the ground pillars — the stone monoliths themselves stay grounded.
      ring(0x8a8472, 0.5);
      pulseMats.push(ring(0x8a5ae0, 0.6, R * 0.66, R * 0.76)); // inner glyph circle
      pulseMats.push(ring(0x8a5ae0, 0.75, R * 0.88, R * 1.04)); // outer rune-ring band — wider + brighter
      rimRing(8, (x, z) => {
        b.part(b.g.box, b.mk('dark', { color: 0x5a4a78 }), { x, z, y: 0.11, sx: 0.07, sy: 0.24, sz: 0.05 }, aura); // stone monolith
      });
      orbiter(4, 0.5, 0.5, 0.3, (grp) => {
        b.part(b.g.box, b.mk('dark', { color: 0x9a6ae8, emissive: 0x9a6ae8, emi: 1.8 }), { sx: 0.08, sy: 0.2, sz: 0.03, noShell: true, shadow: false }, grp); // floating rune tablet
      });
      spin = 0.4;
      break;
    }
    case 5: { // ember-ring (MYTHIC) — twin fiery rings + uneven flame tongues +
      //         RISING ember motes that drift up and fade. Spin + pulse + drift.
      pulseMats.push(ring(0xff5a28, 0.5));
      pulseMats.push(ring(0xffaa3d, 0.35, R * 0.6, R * 0.72)); // inner glow
      rimRing(12, (x, z, a) => b.part(b.g.cone, b.mk('dark', { color: 0xffaa3d, emissive: 0xffaa3d, emi: 1.3, flat: false }), { x, z, y: 0.11, sx: 0.05, sy: 0.18 + Math.abs(Math.sin(a * 3)) * 0.1, sz: 0.05, noShell: true }, aura));
      const motes = [] as { mesh: THREE.Mesh; mat: { opacity: number }; a: number; r: number; phase: number }[];
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const mat = b.basic(0xffc24a, { transparent: true, opacity: 0.9 });
        const mesh = b.part(b.g.sph, mat, { x: Math.sin(a) * R * 0.85, z: Math.cos(a) * R * 0.85, y: 0.1, s: 0.05, noShell: true, shadow: false }, aura);
        motes.push({ mesh, mat, a, r: R * (0.8 + (i % 3) * 0.08), phase: i / 8 });
      }
      // 5 rising flame wisps at STAGGERED heights (mythic — most elements,
      // brightest) — "fire climbs", distinct from the rising motes above.
      const wispMat = b.mk('dark', { color: 0xffaa3d, emissive: 0xffaa3d, emi: 1.2, flat: false });
      const wisps: { mesh: THREE.Mesh; baseY: number; phase: number }[] = [];
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 + 0.3;
        const r = R * 0.55;
        const baseY = 0.25 + (i % 3) * 0.2; // staggered heights
        const mesh = b.part(b.g.cone, wispMat, { x: Math.sin(a) * r, z: Math.cos(a) * r, y: baseY, sx: 0.045, sy: 0.14, sz: 0.045, noShell: true, shadow: false }, aura);
        wisps.push({ mesh, baseY, phase: i / 5 });
      }
      customTicks.push((_dt, t) => {
        for (const mo of motes) {
          const p = (t * 0.4 + mo.phase) % 1;
          mo.mesh.position.set(Math.sin(mo.a) * mo.r, 0.1 + p * 0.6, Math.cos(mo.a) * mo.r);
          mo.mat.opacity = 0.8 * (1 - p);
        }
        for (const w of wisps) {
          w.mesh.position.y = w.baseY + Math.sin(t * 2 + w.phase * 6) * 0.06;
          w.mesh.scale.y = 1 + 0.3 * Math.sin(t * 6 + w.phase * 6);
        }
      });
      spin = 0.5;
      break;
    }
    case 6: // obsidian-band — dark ring + a glowing magma crack ring + bigger
      //         vents + 3 slow dark shards with red emissive edges orbiting.
      ring(0x1a0e14, 0.8);
      pulseMats.push(ring(0xff5a28, 0.45, R * 0.6, R * 0.7)); // molten inner crack
      rimRing(6, (x, z) => b.part(b.g.sph, b.mk('dark', { color: 0xa02418, emissive: 0xff5a28, emi: 1.2, flat: false }), { x, z, y: 0.07, s: 0.1 }, aura));
      orbiter(3, 0.4, 0.7, 0.15, (grp) => {
        b.part(b.g.cone, b.mk('dark', { color: 0x1a0e14, flat: true }), { sx: 0.06, sy: 0.22, sz: 0.06 }, grp);
        b.part(b.g.cone, b.mk('dark', { color: 0xff5a28, emissive: 0xff5a28, emi: 1.0, flat: false }), { y: 0.1, sx: 0.03, sy: 0.06, sz: 0.03, noShell: true, shadow: false }, grp); // red emissive edge
      });
      break;
    case 7: { // crystal-shard (EPIC) — icy ring + inner glow ring + TWO-TIER
      //         upward crystal shards (tall + short), counter-spin + 5
      //         orbiting ice shards at varied heights/tilts (vertical pass).
      ring(0xa0d8ff, 0.5);
      pulseMats.push(ring(0xcfeeff, 0.3, R * 0.64, R * 0.72)); // inner glow
      rimRing(10, (x, z, a) => {
        const tall = Math.sin(a * 5) > 0;
        b.part(b.g.cone, b.mk('dark', { color: 0xcfeeff, emissive: 0x8fb8ff, emi: 0.6, flat: false }), { x, z, y: tall ? 0.14 : 0.1, sx: 0.05, sy: tall ? 0.24 : 0.16, sz: 0.05, rz: Math.sin(a) * 0.2 }, aura);
      });
      const iceMat = b.mk('dark', { color: 0xcfeeff, emissive: 0x8fb8ff, emi: 0.7, flat: false });
      for (let i = 0; i < 5; i++) {
        const phase = (i / 5) * Math.PI * 2 + 0.5;
        const r = R * (0.65 + (i % 2) * 0.1);
        const height = 0.3 + (i % 3) * 0.18; // varied heights
        const tilt = (i % 2 ? 1 : -1) * 0.4;
        const grp = new THREE.Group();
        grp.position.set(Math.sin(phase) * r, height, Math.cos(phase) * r);
        grp.rotation.z = tilt;
        aura.add(grp);
        b.part(b.g.cone, iceMat, { sx: 0.035, sy: 0.22, sz: 0.035, noShell: true, shadow: false }, grp);
        customTicks.push((_dt, t) => {
          const a = phase + t * 0.25;
          grp.position.set(Math.sin(a) * r, height + Math.sin(t * 1.5 + phase * 4) * 0.04, Math.cos(a) * r);
          grp.rotation.y = a;
        });
      }
      spin = -0.3;
      break;
    }
    case 8: { // tide-foam-ring — glowing teal ring + inner foam glow + bigger
      //         crests + 3 tilted foam arcs that ride up/down like waves.
      pulseMats.push(ring(0x3fa0c4, 0.65));
      pulseMats.push(ring(0xcfeef0, 0.3, R * 0.62, R * 0.72)); // inner foam wash
      rimRing(12, (x, z) => b.part(b.g.sph, b.mk('dark', { color: 0xeafcff, emissive: 0x8fd8e8, emi: 0.5, flat: false }), { x, z, y: 0.08, sx: 0.1, sy: 0.07, sz: 0.1 }, aura));
      const foamArcGeo = new THREE.TorusGeometry(0.14, 0.02, 6, 16, Math.PI * 1.3);
      b.own(foamArcGeo);
      const foamArcMat = b.mk('dark', { color: 0xeafcff, emissive: 0x8fd8e8, emi: 0.6, flat: false });
      const arcs: { mesh: THREE.Mesh; a: number; r: number; baseY: number; phase: number }[] = [];
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const r = R * 0.7;
        const baseY = 0.3;
        const mesh = new THREE.Mesh(foamArcGeo, foamArcMat);
        mesh.position.set(Math.sin(a) * r, baseY, Math.cos(a) * r);
        mesh.rotation.x = 0.3;
        aura.add(mesh);
        arcs.push({ mesh, a, r, baseY, phase: i / 3 });
      }
      customTicks.push((_dt, t) => {
        for (const arc of arcs) {
          arc.mesh.position.y = arc.baseY + Math.sin(t * 1.2 + arc.phase * 6) * 0.15; // ride up/down like waves
          arc.mesh.rotation.y = t * 0.2 + arc.phase * 6;
        }
      });
      spin = 0.2;
      break;
    }
    case 9: { // void-eclipse (MYTHIC) — a true ECLIPSE: a near-black eclipsed
      //         disc (solid, occluding — additive OFF) haloed by a BRILLIANT
      //         violet corona with a white-hot inner edge, 4 orbiting violet
      //         spark beacons, and faint corona wisps radiating outward.
      //         Clearly out-classes the epic rings at rest; tick adds slow
      //         spark orbit + corona shimmer.
      const discGeo = new THREE.CircleGeometry(R * 0.6, 40);
      b.own(discGeo);
      const discMat = b.basic(0x08061a);
      const disc = new THREE.Mesh(discGeo, discMat);
      disc.rotation.x = -Math.PI / 2;
      disc.position.y = 0.033;
      disc.renderOrder = -1;
      aura.add(disc);
      // white-hot inner edge, hugging the disc's rim
      const edgeGeo = new THREE.RingGeometry(R * 0.6, R * 0.66, 48);
      b.own(edgeGeo);
      const edgeMat = b.basic(0xf5edff, { transparent: true, opacity: 0.95 });
      edgeMat.blending = THREE.AdditiveBlending;
      edgeMat.depthWrite = false;
      edgeMat.toneMapped = false;
      const edge = new THREE.Mesh(edgeGeo, edgeMat);
      edge.rotation.x = -Math.PI / 2;
      edge.position.y = 0.038;
      aura.add(edge);
      pulseMats.push(edgeMat);
      pulseMats.push(ring(0x9a5ce8, 0.65, R * 0.72, R * 0.9)); // brilliant violet corona
      // faint corona wisps radiating outward past the ring
      rimRing(5, (x, z, a) => {
        const wispMat = b.basic(0xc9a6ff, { transparent: true, opacity: 0.3 });
        wispMat.blending = THREE.AdditiveBlending;
        wispMat.depthWrite = false;
        wispMat.toneMapped = false;
        b.part(b.g.cone, wispMat, { x: x * 1.05, z: z * 1.05, y: 0.05, sx: 0.055, sy: 0.42, sz: 0.055, rx: -Math.PI / 2, ry: a, noShell: true, shadow: false }, aura);
      });
      // 4 orbiting violet spark beacons
      const sparks: { mesh: THREE.Mesh; r: number; phase: number }[] = [];
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.4;
        const r = R * 0.8;
        const mesh = b.part(b.g.sph, b.mk('dark', { color: 0xb890ff, emissive: 0xb890ff, emi: 1.1, flat: false }), { x: Math.sin(a) * r, z: Math.cos(a) * r, y: 0.12, s: 0.075, noShell: true, shadow: false }, aura);
        sparks.push({ mesh, r, phase: a });
      }
      customTicks.push((_dt, t) => {
        for (const sp of sparks) {
          const a = sp.phase + t * 0.6;
          sp.mesh.position.set(Math.sin(a) * sp.r, 0.12 + Math.sin(t * 2 + sp.phase) * 0.03, Math.cos(a) * sp.r);
        }
      });
      // 4 orbiting obsidian shards (dark tetra-cones, violet emissive edge) —
      // mythic, most elements/brightest — plus a thin corona ring tilted
      // ~25° off-horizontal so the eclipse reads with real depth.
      orbiter(4, 0.5, 0.55, 0.35, (grp) => {
        b.part(b.g.cone, b.mk('dark', { color: 0x140a22, flat: true }), { sx: 0.05, sy: 0.16, sz: 0.05 }, grp);
        b.part(b.g.cone, b.mk('dark', { color: 0x9a5ce8, emissive: 0x9a5ce8, emi: 1.0, flat: false }), { y: 0.08, sx: 0.02, sy: 0.05, sz: 0.02, noShell: true, shadow: false }, grp); // violet emissive edge
      });
      const tiltRingGeo = new THREE.RingGeometry(R * 0.75, R * 0.82, 40);
      b.own(tiltRingGeo);
      const tiltRingMat = b.basic(0xb890ff, { transparent: true, opacity: 0.4 });
      tiltRingMat.blending = THREE.AdditiveBlending;
      tiltRingMat.depthWrite = false;
      tiltRingMat.toneMapped = false;
      const tiltRing = new THREE.Mesh(tiltRingGeo, tiltRingMat);
      tiltRing.rotation.x = -Math.PI / 2 + (25 * Math.PI) / 180;
      tiltRing.position.y = 0.15;
      aura.add(tiltRing);
      pulseMats.push(tiltRingMat);
      spin = 0.22;
      break;
    }
    case 10: // phoenix-feather (EPIC) — gold-fire ring + TWO-TONE plumes (red
      //         body + gold inner tip), spin + pulse + 4 circling feather
      //         blades (smaller two-cone construction) orbiting at height.
      pulseMats.push(ring(0xffaa3d, 0.5));
      rimRing(10, (x, z, a) => {
        b.part(b.g.cone, b.mk('dark', { color: 0xff5a28, emissive: 0xff7a2a, emi: 1.1, flat: false }), { x, z, y: 0.13, sx: 0.05, sy: 0.26, sz: 0.04, rz: Math.sin(a) * 0.15 }, aura);
        b.part(b.g.cone, b.mk('dark', { color: 0xffd770, emissive: 0xffd770, emi: 0.9, flat: false }), { x: x * 0.92, z: z * 0.92, y: 0.16, sx: 0.03, sy: 0.16, sz: 0.025, rz: Math.sin(a) * 0.15, noShell: true }, aura); // gold inner tip
      });
      orbiter(4, 0.5, 0.6, 0.3, (grp) => {
        b.part(b.g.cone, b.mk('dark', { color: 0xff5a28, emissive: 0xff7a2a, emi: 1.0, flat: false }), { sx: 0.035, sy: 0.18, sz: 0.03, noShell: true, shadow: false }, grp);
        b.part(b.g.cone, b.mk('dark', { color: 0xffd770, emissive: 0xffd770, emi: 0.8, flat: false }), { y: 0.03, sx: 0.02, sy: 0.11, sz: 0.018, noShell: true, shadow: false }, grp);
      });
      spin = 0.4;
      break;
    case 11: { // gilded-rose-ring (EPIC) — gold ring (pulses) + layered rose
      //         buds (3 petal tiers) flanked by gold leaves + floating gold
      //         petals spiraling upward.
      pulseMats.push(ring(0xe8c870, 0.6));
      rimRing(6, (x, z) => {
        b.part(b.g.sph, b.mk('dark', { color: 0xb84a66, flat: false }), { x, z, y: 0.07, s: 0.09 }, aura); // outer petals
        b.part(b.g.sph, b.mk('dark', { color: 0xd87890, flat: false }), { x, z, y: 0.095, s: 0.06 }, aura); // mid
        b.part(b.g.sph, b.mk('dark', { color: 0xf0a8b8, flat: false }), { x, z, y: 0.115, s: 0.03 }, aura); // inner bud
        b.part(b.g.sph, b.mk('dark', { color: 0xe8c870, flat: false }), { x: x * 0.86, z: z * 0.86, y: 0.05, sx: 0.05, sy: 0.02, sz: 0.09 }, aura); // gold leaf
      });
      const petalMat = b.mk('dark', { color: 0xe8c870, emissive: 0xe8c870, emi: 0.5, flat: false });
      const petals: { mesh: THREE.Mesh; a: number; r: number; phase: number }[] = [];
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        const r = R * 0.6;
        const y = 0.2 + (i % 3) * 0.15;
        const mesh = b.part(b.g.cyl8, petalMat, { x: Math.sin(a) * r, z: Math.cos(a) * r, y, sx: 0.06, sy: 0.012, sz: 0.06, noShell: true, shadow: false }, aura);
        petals.push({ mesh, a, r, phase: i / 5 });
      }
      customTicks.push((_dt, t) => {
        for (const p of petals) {
          const cyc = (t * 0.12 + p.phase) % 1;
          const ang = p.a + t * 0.25;
          p.mesh.position.set(Math.sin(ang) * p.r, 0.2 + cyc * 0.65, Math.cos(ang) * p.r);
          p.mesh.rotation.y = t * 2 + p.phase * 6;
        }
      });
      break;
    }
  }

  const tick =
    spin !== 0 || pulseMats.length > 0 || customTicks.length > 0
      ? (dt: number, elapsed: number): void => {
          if (spin !== 0) aura.rotation.y += spin * dt;
          if (pulseMats.length > 0) {
            // brighter pulse floor (0.55..0.9) so the ring stays clearly visible
            // through the whole cycle instead of fading to a faint hairline.
            const k = 0.6 + 0.3 * Math.sin(elapsed * 3.2);
            for (const mm of pulseMats) mm.opacity = k;
          }
          for (const fn of customTicks) fn(dt, elapsed);
        }
      : undefined;
  return tick ? { group: aura, tick } : { group: aura };
}

// ───────────────────────── backgrounds (lobby-card only) ─────────────────────────

/**
 * A lobby BACKGROUND is a 2D avatar-card BACKDROP (gradient / starfield /
 * rays). It has no natural in-game place — the character lives in a real 3D
 * tavern arena, not on a card — so by DEFAULT the 3D figure renders NOTHING
 * for `icon.background`. This is a deliberate, COMPLETE no-op mapping: all 8
 * ids are catalogued in {@link BACKGROUND_IDS} (so the system is aware of
 * every backdrop) and {@link backgroundDiscColor} exposes a representative
 * tint for each, but they stay lobby-card-only.
 *
 * OPTIONAL: a game may pass `iconBackgroundDisc: true` to {@link
 * createFigure}, which calls {@link buildBackgroundDisc} to lay a faint themed
 * ground disc under the character (a subtle nod to the backdrop) — off by
 * default so it never forces visual clutter into fast gameplay.
 */
export function backgroundDiscColor(background: number | string | undefined): number | null {
  const idx = resolveCosmetic(background, BACKGROUND_IDS, BACKGROUND_COUNT);
  if (idx < 0) return null;
  // Representative tint per backdrop (mirrors the 2D gradient's dominant hue).
  return [0xa04020, 0x3a6028, 0x1a2454, 0x4a0a0a, 0xdec59a, 0x2a0e44, 0x3a8a9e, 0x2a1858][idx] ?? null;
}

/**
 * OPTIONAL faint themed ground disc for a background cosmetic (off by default;
 * see {@link backgroundDiscColor}). Adds one translucent disc under the feet
 * in the backdrop's dominant color. Renders nothing for none/unknown.
 */
export function buildBackgroundDisc(
  b: CharPartCtx,
  background: number | string | undefined,
  opts: { parent: THREE.Object3D; radius?: number },
): void {
  const color = backgroundDiscColor(background);
  if (color === null) return;
  const R = opts.radius ?? 1.05;
  const g = new THREE.CircleGeometry(R, 32);
  b.own(g);
  const mat = b.basic(color, { transparent: true, opacity: 0.22 });
  const mesh = new THREE.Mesh(g, mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = 0.015; // under the contact halo / border ring
  mesh.renderOrder = -2;
  opts.parent.add(mesh);
}

/**
 * A built backdrop / particle / rim effect: its group + an optional per-frame
 * updater (twinkling stars, rising embers, drifting motes). Mirrors
 * {@link BorderAura}. Returned by {@link buildBackground} / {@link buildParticles}.
 */
export interface FigureFx {
  group: THREE.Object3D;
  tick?: (dt: number, elapsed: number) => void;
}

/**
 * Build a REAL premium 3D BACKDROP behind the character for a lobby BACKGROUND
 * cosmetic (or a repurposed seat-background). Unlike {@link buildBackgroundDisc}
 * (a faint ground disc), this stands a themed pedestal under the feet + a curved
 * back-panel "curtain" behind the figure + a few theme accents (a star dome, an
 * ember arch, scroll rolls, velvet folds …) so the locker / shop / profile
 * render reads as a staged showcase. Kept DIM (low emissive, dark panel) so it
 * frames the character without washing it out. Attaches under `opts.parent`
 * (the figure ROOT). Unknown / none → null. Every mesh is flagged
 * `userData.ttDecor` so the locker framing excludes it from the character bounds.
 */
export function buildBackground(
  b: CharPartCtx,
  background: number | string | undefined,
  opts: { parent: THREE.Object3D },
): FigureFx | null {
  const idx = resolveCosmetic(aliasBackgroundId(background), BACKGROUND_IDS, BACKGROUND_COUNT);
  if (idx < 0) return null;
  const g = b.g;
  const grp = new THREE.Group();
  grp.name = 'tt-backdrop';
  opts.parent.add(grp);
  const ticks: ((dt: number, elapsed: number) => void)[] = [];
  // Flag every backdrop mesh so the locker's character-bounds framing skips it.
  const D = (mesh: THREE.Mesh): THREE.Mesh => { mesh.userData.ttDecor = true; return mesh; };
  // Backdrop meshes opt OUT of the outline shell + shadows (a backdrop with a
  // toon contour reads as a cut-out prop, not a soft stage).
  const panel = (color: number, emissive?: number, emi = 0.0): THREE.Mesh =>
    D(b.part(g.sph, b.mk('dark', { color, flat: false, ...(emissive !== undefined ? { emissive, emi } : {}) }),
      { x: 0, y: 0.95, z: -1.15, sx: 1.95, sy: 2.0, sz: 0.45, noShell: true, shadow: false }, grp));
  const pedestal = (color: number, emissive?: number, emi = 0.0): void => {
    D(b.part(g.cyl8, b.mk('dark', { color, flat: false, ...(emissive !== undefined ? { emissive, emi } : {}) }),
      { x: 0, y: 0.06, z: 0, sx: 0.92, sy: 0.12, sz: 0.92, noShell: true, shadow: false }, grp)); // riser
    D(b.part(g.cyl8, b.mk('dark', { color: shadeHex(color, 0.25), flat: false }),
      { x: 0, y: 0.13, z: 0, sx: 0.74, sy: 0.05, sz: 0.74, noShell: true, shadow: false }, grp)); // top step
  };
  const glowDisc = (color: number, op = 0.4): void => {
    const cg = new THREE.CircleGeometry(0.9, 28);
    b.own(cg);
    const mat = b.basic(color, { transparent: true, opacity: op });
    const mesh = new THREE.Mesh(cg, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(0, 0.18, 0);
    mesh.renderOrder = -1;
    D(mesh);
    grp.add(mesh);
  };
  const star = (x: number, y: number, s: number, color = 0xe8dcff): THREE.Mesh =>
    D(b.part(g.sph, b.basic(color), { x, y, z: -1.0, s, noShell: true, shadow: false }, grp));

  switch (idx) {
    case 0: // tavern-hearth — warm stone backdrop + ember hearth glow
      pedestal(0x4a3324);
      panel(0x3a2418);
      glowDisc(0xff7a32, 0.32);
      for (const sx of [-0.55, 0, 0.55]) // ember logs glowing at the hearth base
        D(b.part(g.cyl, b.mk('dark', { color: 0xff6a28, emissive: 0xff5a28, emi: 0.7, flat: false }), { x: sx, y: 0.32, z: -0.95, sx: 0.34, sy: 0.1, sz: 0.1, rz: Math.PI / 2, noShell: true, shadow: false }, grp));
      break;
    case 1: { // forest-grove — green canopy backdrop + two flanking trees
      pedestal(0x3a4a28);
      panel(0x24401e);
      glowDisc(0x4a8a3a, 0.22);
      for (const sx of [-1.0, 1.0]) {
        D(b.part(g.cyl, b.mk('dark', { color: 0x5a4326, flat: false }), { x: sx, y: 0.5, z: -0.9, sx: 0.12, sy: 1.0, sz: 0.12, noShell: true, shadow: false }, grp)); // trunk
        for (const [dy, s] of [[0.95, 0.5], [1.2, 0.4], [1.4, 0.28]] as [number, number][])
          D(b.part(g.cone, b.mk('dark', { color: 0x3f7a32, flat: false }), { x: sx, y: dy, z: -0.9, sx: s, sy: s * 1.1, sz: s, noShell: true, shadow: false }, grp)); // foliage tiers
      }
      break;
    }
    case 2: { // starfield — deep-blue star DOME + a moon + twinkling stars +
      //         a nebula wisp + a shooting star. Tile-contrast pass v2: the
      //         panel is lifted from near-black to a readable deep-indigo
      //         NIGHT SKY, stars are more numerous with two big
      //         warm/cool ones carrying an additive halo, plus a streaking
      //         shooting star — reads as sky, not a rendering bug.
      pedestal(0x232a48);
      panel(0x141b38, 0x24356c, 0.32);
      D(b.part(g.sph, b.mk('dark', { color: 0xe8ecff, emissive: 0xc0ccff, emi: 0.7, flat: false }), { x: 0.7, y: 1.7, z: -1.0, s: 0.28, noShell: true, shadow: false }, grp)); // moon
      const neb = b.basic(0x5a7cff, { transparent: true, opacity: 0.24 });
      D(b.part(g.sph, neb, { x: -0.35, y: 1.35, z: -1.0, sx: 0.65, sy: 0.4, sz: 0.06, noShell: true, shadow: false }, grp)); // nebula wisp
      const starDefs: [number, number, number, number][] = [
        [-0.7, 1.5, 0.05, 0], [-0.4, 1.9, 0.045, 1], [0.1, 1.75, 0.09, 0], [-0.9, 1.05, 0.045, 1],
        [0.5, 1.15, 0.05, 0], [0.9, 1.55, 0.045, 1], [-0.2, 1.35, 0.085, 1], [0.3, 1.95, 0.05, 0],
        [-0.55, 1.85, 0.04, 1], [0.75, 1.05, 0.04, 0],
      ];
      const starCols = [0xfff6e0, 0xbfd8ff];
      const stars: THREE.Mesh[] = [];
      for (const [x, y, s, ci] of starDefs) {
        const col = starCols[ci]!;
        stars.push(star(x, y, s, col));
        if (s > 0.07) { // the two BIG stars get a soft additive halo so they read as bright
          const haloMat = b.basic(col, { transparent: true, opacity: 0.35 });
          haloMat.blending = THREE.AdditiveBlending;
          haloMat.depthWrite = false;
          haloMat.toneMapped = false;
          D(b.part(g.sph, haloMat, { x, y, z: -1.02, s: s * 2.2, noShell: true, shadow: false }, grp));
        }
      }
      const starMats = stars.map((m) => m.material as THREE.MeshBasicMaterial);
      // a clear shooting-star streak with a fading tail, sweeping every ~4s
      // opacity starts non-zero (not 0) — the offscreen thumbnail baker never
      // calls tick(), so a start-at-0 streak would never appear in a baked
      // tile; this way it's always at least faintly visible there too, and
      // still pulses brighter/dimmer live via the tick below.
      const shootMat = b.basic(0xffffff, { transparent: true, opacity: 0.65 });
      shootMat.blending = THREE.AdditiveBlending;
      shootMat.depthWrite = false;
      shootMat.toneMapped = false;
      const shoot = D(b.part(g.box, shootMat, { x: -0.3, y: 1.9, z: -0.98, sx: 0.26, sy: 0.032, sz: 0.01, rz: -0.5, noShell: true, shadow: false }, grp));
      ticks.push((_dt, t) => {
        neb.opacity = 0.18 + 0.12 * (0.5 + 0.5 * Math.sin(t * 1.1));
        for (let i = 0; i < starMats.length; i++) starMats[i]!.opacity = 0.65 + 0.35 * (0.5 + 0.5 * Math.sin(t * 2 + i * 1.3));
        const cyc = (t * 0.25) % 1;
        const streak = cyc < 0.18 ? Math.sin((cyc / 0.18) * Math.PI) : 0;
        shootMat.opacity = streak * 0.9;
        shoot.position.set(-0.3 + cyc * 1.3, 1.9 - cyc * 0.7, -0.98);
      });
      break;
    }
    case 3: { // forges-maw — a real furnace MAW: FLOODED with heat (only the
      //         outer rim stays deep red — the previous pass over-corrected
      //         "flat" into "dark", which read as a near-black tile; this one
      //         must be the WARMEST tile in the row). A big bright molten
      //         gradient fills most of the panel (core 0xffb050 white-hot →
      //         mids 0xff6a20), a thick hot-white mouth line, a rim-lit dark
      //         iron door-frame (silhouettes instead of vanishing), and ember
      //         specks seeded at visible rising heights.
      pedestal(0x241512);
      panel(0x7a2410, 0x5a1a08, 0.3); // warm red-orange furnace wall, brighter base
      // molten gradient — a BIG mid glow filling most of the panel, brightest
      // low/center where a small hyper-bright core sits
      const midGeo = new THREE.CircleGeometry(1.05, 28);
      b.own(midGeo);
      const midMat = b.basic(0xff6a20, { transparent: true, opacity: 0.7 });
      midMat.blending = THREE.AdditiveBlending;
      midMat.depthWrite = false;
      midMat.toneMapped = false;
      const mid = new THREE.Mesh(midGeo, midMat);
      mid.position.set(0, 0.95, -1.1);
      mid.renderOrder = -1;
      D(mid);
      grp.add(mid);
      const coreGeo = new THREE.CircleGeometry(0.55, 24);
      b.own(coreGeo);
      const coreMat = b.basic(0xffb050, { transparent: true, opacity: 0.92 });
      coreMat.blending = THREE.AdditiveBlending;
      coreMat.depthWrite = false;
      coreMat.toneMapped = false;
      const core = new THREE.Mesh(coreGeo, coreMat);
      core.position.set(0, 0.6, -1.08);
      D(core);
      grp.add(core);
      // thick white-hot horizontal glow line at the mouth
      const lineMat = b.basic(0xfff8e0, { transparent: true, opacity: 0.92 });
      lineMat.blending = THREE.AdditiveBlending;
      lineMat.depthWrite = false;
      lineMat.toneMapped = false;
      D(b.part(g.box, lineMat, { x: 0, y: 0.3, z: -1.06, sx: 0.78, sy: 0.06, sz: 0.02, noShell: true, shadow: false }, grp));
      // dark iron door-frame capping the top rim — RIM-LIT (slight emissive)
      // so it reads as a silhouette against the glow instead of vanishing
      const ironCol = 0x241610;
      for (const sx of [-0.85, 0.85])
        D(b.part(g.box, b.mk('dark', { color: ironCol, flat: false, emissive: 0x3a1408, emi: 0.3 }), { x: sx, y: 1.05, z: -1.05, sx: 0.16, sy: 2.0, sz: 0.22, noShell: true, shadow: false }, grp)); // side pillar
      D(b.part(g.box, b.mk('dark', { color: ironCol, flat: false, emissive: 0x3a1408, emi: 0.3 }), { x: 0, y: 2.05, z: -1.05, sx: 1.86, sy: 0.2, sz: 0.24, noShell: true, shadow: false }, grp)); // top lintel
      // rising ember specks — seeded at visible heights/opacities so the tile
      // shows them mid-flight, not stacked at a start position
      const emberDefs: [number, number, number][] = [[-0.35, 0.65, 0.1], [0.3, 0.95, 0.08], [-0.1, 1.25, 0.07], [0.42, 0.45, 0.09]];
      const embers = emberDefs.map(([ex, ey0, es]) => {
        const mat = b.basic(0xffc46a, { transparent: true, opacity: 0.85 });
        mat.blending = THREE.AdditiveBlending;
        mat.depthWrite = false;
        mat.toneMapped = false;
        const mesh = D(b.part(g.sph, mat, { x: ex, y: ey0, z: -1.0, s: es, noShell: true, shadow: false }, grp));
        return { mesh, mat, baseY: ey0, phase: ex * 3.1 };
      });
      ticks.push((_dt, t) => {
        midMat.opacity = 0.6 + 0.2 * (0.5 + 0.5 * Math.sin(t * 2.2));
        coreMat.opacity = 0.8 + 0.2 * (0.5 + 0.5 * Math.sin(t * 2.6));
        lineMat.opacity = 0.75 + 0.25 * (0.5 + 0.5 * Math.sin(t * 3.0));
        for (const em of embers) {
          const p = (t * 0.18 + em.phase * 0.1) % 1;
          em.mesh.position.y = em.baseY + p * 0.5;
          em.mat.opacity = 0.85 * (1 - p);
        }
      });
      break;
    }
    case 4: // parchment-scroll — tan parchment panel + two scroll rolls
      pedestal(0x6a5634);
      panel(0x6a5634);
      for (const sx of [-0.95, 0.95])
        D(b.part(g.cyl, b.mk('dark', { color: 0x4e3f24, flat: false }), { x: sx, y: 0.95, z: -1.0, sx: 0.18, sy: 1.9, sz: 0.18, noShell: true, shadow: false }, grp)); // scroll rod
      break;
    case 5: // velvet-drape — purple curtain folds
      pedestal(0x3a1a52);
      panel(0x2a1240);
      for (const [i, sx] of [-0.7, -0.23, 0.23, 0.7].entries())
        D(b.part(g.cyl, b.mk('dark', { color: i % 2 ? 0x3a1a58 : 0x4a2470, flat: false }), { x: sx, y: 0.95, z: -0.95, sx: 0.34, sy: 1.95, sz: 0.18, noShell: true, shadow: false }, grp)); // fold
      break;
    case 6: { // tide-pool-wash — teal backdrop + foam crests at the waterline
      pedestal(0x1e4450);
      panel(0x123a44, 0x0e3a4a, 0.16);
      glowDisc(0x2a8a9e, 0.28);
      for (const sx of [-0.7, -0.25, 0.25, 0.7])
        D(b.part(g.sph, b.mk('dark', { color: 0xcfeef0, flat: false }), { x: sx, y: 0.34, z: -0.9, sx: 0.16, sy: 0.1, sz: 0.1, noShell: true, shadow: false }, grp)); // foam
      break;
    }
    case 7: { // cosmos-veil — deep violet void + swirling nebula + stars.
      //         Tile-contrast pass: brighter nebula + bigger/brighter stars
      //         with a raised opacity floor so it reads premium, not black.
      pedestal(0x241848);
      panel(0x1a1040, 0x3a2472, 0.3);
      const neb = b.basic(0xac6cff, { transparent: true, opacity: 0.42 });
      const neb2 = b.basic(0x5a7cff, { transparent: true, opacity: 0.34 });
      D(b.part(g.sph, neb, { x: -0.3, y: 1.3, z: -1.0, sx: 0.7, sy: 0.5, sz: 0.06, noShell: true, shadow: false }, grp));
      D(b.part(g.sph, neb2, { x: 0.35, y: 1.6, z: -1.0, sx: 0.55, sy: 0.42, sz: 0.06, noShell: true, shadow: false }, grp));
      const stars: THREE.Mesh[] = [];
      for (const [x, y, s] of [[-0.6, 1.7, 0.045], [0.2, 1.85, 0.04], [0.7, 1.3, 0.045], [-0.85, 1.2, 0.04], [0.5, 1.95, 0.04], [-0.1, 1.45, 0.04]] as [number, number, number][])
        stars.push(star(x, y, s));
      const starMats = stars.map((m) => m.material as THREE.MeshBasicMaterial);
      ticks.push((_dt, t) => {
        neb.opacity = 0.32 + 0.16 * (0.5 + 0.5 * Math.sin(t * 1.2));
        for (let i = 0; i < starMats.length; i++) starMats[i]!.opacity = 0.55 + 0.4 * (0.5 + 0.5 * Math.sin(t * 2.4 + i * 1.7));
      });
      break;
    }
    case 8: { // eclipse-veil (MYTHIC) — a dark eclipsed MOON dominates the
      //         backdrop, haloed by a BRIGHT violet corona ring, with a
      //         scattering of twinkling stars beyond. Distinct from
      //         cosmos-veil (nebula-led) so "Eclipse" reads as an eclipse.
      pedestal(0x140a22);
      panel(0x0e0818, 0x241340, 0.22);
      D(b.part(g.sph, b.mk('dark', { color: 0x0c0714, emissive: 0x1a0e28, emi: 0.35, flat: false }), { x: 0, y: 1.55, z: -1.0, s: 0.5, noShell: true, shadow: false }, grp)); // eclipsed disc
      const coronaGeo = new THREE.RingGeometry(0.5, 0.64, 40);
      b.own(coronaGeo);
      const coronaMat = b.basic(0xbf9aff, { transparent: true, opacity: 0.85 });
      coronaMat.blending = THREE.AdditiveBlending;
      coronaMat.depthWrite = false;
      coronaMat.toneMapped = false;
      const corona = new THREE.Mesh(coronaGeo, coronaMat);
      corona.position.set(0, 1.55, -0.99);
      corona.renderOrder = -1;
      D(corona);
      grp.add(corona);
      const stars: THREE.Mesh[] = [];
      for (const [x, y, s] of [[-0.75, 1.3, 0.04], [0.8, 1.75, 0.04], [-0.4, 1.9, 0.035], [0.55, 1.15, 0.04], [-0.95, 1.65, 0.035]] as [number, number, number][])
        stars.push(star(x, y, s));
      const starMats = stars.map((m) => m.material as THREE.MeshBasicMaterial);
      ticks.push((_dt, t) => {
        coronaMat.opacity = 0.65 + 0.3 * (0.5 + 0.5 * Math.sin(t * 2.4));
        for (let i = 0; i < starMats.length; i++) starMats[i]!.opacity = 0.55 + 0.4 * (0.5 + 0.5 * Math.sin(t * 2 + i * 1.4));
      });
      break;
    }
    case 9: { // arcane-circle — a dark violet circle backdrop carrying an
      //         actual GLOWING GOLD magic sigil (double ring + 4 wide glyph
      //         glints clearing the head silhouette), pulsing. WARM gold on
      //         dark violet reads clearly distinct from eclipse-veil's COOL
      //         lavender corona (same shape language, different palette so
      //         the two mythics don't read as the same reskinned item), and
      //         distinct from velvet-drape (a plain curtain, no iconography).
      pedestal(0x1c1030);
      panel(0x140a24, 0x3a1e70, 0.24);
      const sigilGeo = new THREE.RingGeometry(0.5, 0.6, 40);
      b.own(sigilGeo);
      const sigilMat = b.basic(0xe8b84a, { transparent: true, opacity: 0.9 });
      sigilMat.blending = THREE.AdditiveBlending;
      sigilMat.depthWrite = false;
      sigilMat.toneMapped = false;
      const sigil = new THREE.Mesh(sigilGeo, sigilMat);
      sigil.position.set(0, 1.35, -1.0);
      sigil.renderOrder = -1;
      D(sigil);
      grp.add(sigil);
      const innerGeo = new THREE.RingGeometry(0.26, 0.33, 32);
      b.own(innerGeo);
      const innerMat = b.basic(0xfde9a8, { transparent: true, opacity: 0.75 });
      innerMat.blending = THREE.AdditiveBlending;
      innerMat.depthWrite = false;
      innerMat.toneMapped = false;
      const inner = new THREE.Mesh(innerGeo, innerMat);
      inner.position.set(0, 1.35, -0.99);
      inner.renderOrder = -1;
      D(inner);
      grp.add(inner);
      for (let i = 0; i < 4; i++) { // glyph glints ringing the sigil, wide
        // enough (r 0.78) to clear the head and read beside the silhouette
        const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
        D(b.part(g.box, b.mk('dark', { color: 0xe8b84a, emissive: 0xe8b84a, emi: 1.3, flat: false }), { x: Math.sin(a) * 0.78, y: 1.35 + Math.cos(a) * 0.78, z: -0.98, sx: 0.065, sy: 0.16, sz: 0.02, noShell: true, shadow: false }, grp));
      }
      ticks.push((_dt, t) => {
        sigilMat.opacity = 0.65 + 0.3 * (0.5 + 0.5 * Math.sin(t * 2.2));
        innerMat.opacity = 0.55 + 0.3 * (0.5 + 0.5 * Math.sin(t * 2.2 + 1.0));
      });
      break;
    }
  }

  const tick = ticks.length > 0 ? (dt: number, t: number): void => { for (const fn of ticks) fn(dt, t); } : undefined;
  return tick ? { group: grp, tick } : { group: grp };
}

// ───────────────────────── ambient particles ─────────────────────────

/**
 * The physical FORM of one mote. Each theme picks a shape so the effect
 * actually LOOKS like its name (a coin is a spinning disc, a card is a
 * tumbling flat rectangle, a petal is a curled leaf, lightning is a jagged
 * bolt) rather than every effect being an identical colored dot.
 */
type ParticleShape =
  | 'orb'      // round mote (fireflies, bubbles, shadows, snow)
  | 'streak'   // thin tall sliver (unused — kept for future themes)
  | 'flake'    // small flattened chip (unused — kept for future themes)
  | 'card'     // flat rectangle (card shimmer — bespoke, kept as-is)
  | 'petal'    // flat ellipse disc (petals)
  | 'coin'     // flat disc (coins)
  | 'feather'  // long tapered quill (phoenix feathers)
  | 'rune'     // glowing glyph tile (runes — bespoke, kept as-is)
  | 'bolt'     // jagged lightning bolt (lightning)
  | 'teardrop' // tapered flame drop (embers)
  | 'diamond'  // elongated bipyramid (gold sparks)
  | 'square';  // flat square chip (confetti)

/** How a mote rotates each frame. */
type SpinMode =
  | 'none'   // no spin (round motes, streaks)
  | 'tumble' // free tumble on 2 axes (cards, confetti, petals, feathers)
  | 'coin'   // spin about the vertical (coins flash face↔edge)
  | 'slow';  // gentle roll (snow, runes)

interface ParticleTheme {
  shape: ParticleShape;
  /** Instance count at FULL (showcase) density. */
  count: number;
  /** Base world size of one mote. */
  size: number;
  /** Vertical drift world u/s: +up (embers/sparks/bubbles), -down (snow/petals/coins), ~0 hover. */
  vy: number;
  /** Horizontal sway amplitude (world u). */
  sway: number;
  /** Per-instance color palette (variety + shimmer; e.g. confetti is multi-hued). */
  colors: number[];
  /** Additive glow (embers/sparks/fireflies/lightning/runes/phoenix) vs solid material. */
  glow: boolean;
  opacity: number;
  spin: SpinMode;
  /** Shared-material opacity twinkle depth 0..1. */
  twinkle: number;
  /** Per-instance size blink (fireflies flicker on/off individually). */
  blink?: boolean;
  /** Sharp per-instance on/off flashes (lightning strikes). */
  strobe?: boolean;
  /** Shrink at the drift extremes for a seamless wrap (drifting motes). */
  fade?: boolean;
}

/**
 * PER-PARTICLE THEME table — audited so every effect matches its catalog NAME.
 * (id → what it renders): embers = rising warm ember orbs; snow = falling white
 * flakes; sparks = fast rising gold slivers; cards = tumbling cream cards;
 * fireflies = hovering green orbs that blink; petals = falling pink petals;
 * bubbles = rising translucent bubbles; lightning = jagged strobing bolts;
 * confetti = falling MULTI-COLOR tumbling flakes; runes = floating glowing
 * violet glyph tiles; phoenix-feathers = drifting fiery quills; shadows =
 * drifting dark wisps with a glowing violet edge; coins = falling SPINNING
 * gold discs.
 */
/**
 * Modest additive under-glow tier (1 subtle → 3 strongest) for the generic
 * instanced-pool themes, keyed loosely to their catalog rarity (uncommon → 1,
 * fine → 2, rare → 3). See the glow-pool note in {@link buildParticles}: once
 * the instanced pool actually seeds real per-instance transforms at
 * construction (below), themes that used to look good ONLY because of an
 * accidental identity-transform blob (gold sparks, fireflies) need a
 * deliberate glow to replace that, so the fix never reads as a downgrade.
 */
const PARTICLE_GLOW_TIER: Record<string, number> = {
  embers: 1, snow: 1, petals: 1, bubbles: 1,
  sparks: 2, cards: 2, fireflies: 2, confetti: 2, coins: 2,
  runes: 3,
};

const PARTICLE_THEME: Record<string, ParticleTheme> = {
  // embers: glowing orange TEARDROPS (was a round orb) — a shape that reads
  // as fire rising, not a generic dot.
  embers:             { shape: 'teardrop', count: 26, size: 0.09,  vy: 0.36,  sway: 0.12, colors: [0xff7a2a, 0xff9a3a, 0xffc24a], glow: true,  opacity: 0.95, spin: 'none',   twinkle: 0.35, fade: true },
  // snow: SOFT WHITE SPHERES (was a flattened flake) — varied sizes already
  // come free from the per-instance size jitter every theme gets.
  snow:               { shape: 'orb',     count: 34, size: 0.075, vy: -0.28, sway: 0.13, colors: [0xffffff, 0xdcefff, 0xbfe0ff], glow: false, opacity: 0.92, spin: 'slow',   twinkle: 0.1,  fade: true },
  // sparks: small bright elongated DIAMONDS (was a thin streak) — an actual
  // faceted gem silhouette.
  sparks:             { shape: 'diamond', count: 24, size: 0.11,  vy: 0.55,  sway: 0.07, colors: [0xffd166, 0xfff0b0],           glow: true,  opacity: 1.0,  spin: 'none',   twinkle: 0.5,  fade: true },
  cards:              { shape: 'card',    count: 16, size: 0.17,  vy: -0.24, sway: 0.24, colors: [0xf4ecd8, 0xf0d68a, 0xe8c060], glow: false, opacity: 0.96, spin: 'tumble', twinkle: 0.4,  fade: true },
  fireflies:          { shape: 'orb',     count: 18, size: 0.075, vy: 0.05,  sway: 0.2,  colors: [0xd9ff8a, 0xbfff6a, 0xeaff9a], glow: true,  opacity: 0.95, spin: 'none',   twinkle: 0.0,  blink: true },
  petals:             { shape: 'petal',   count: 22, size: 0.15,  vy: -0.22, sway: 0.22, colors: [0xffb3d0, 0xff8fb8, 0xffc9de], glow: false, opacity: 0.96, spin: 'tumble', twinkle: 0.0,  fade: true },
  bubbles:            { shape: 'orb',     count: 20, size: 0.12,  vy: 0.3,   sway: 0.12, colors: [0xbfe8ff, 0xdff4ff, 0xa8dcff], glow: false, opacity: 0.5,  spin: 'none',   twinkle: 0.25, fade: true },
  lightning:          { shape: 'bolt',    count: 14, size: 0.66,  vy: 0.0,   sway: 0.05, colors: [0xffffff, 0xcdeaff, 0x9ad0ff], glow: true,  opacity: 1.0,  spin: 'none',   twinkle: 0.0,  strobe: true },
  // confetti: mixed-color tiny flat SQUARES (was a rectangular card shape),
  // distinct from card shimmer's cards.
  confetti:           { shape: 'square',  count: 30, size: 0.1,   vy: -0.32, sway: 0.24, colors: [0xff5a5a, 0x5ad1ff, 0xffd166, 0x8aff8a, 0xd08aff, 0xff9a3a], glow: false, opacity: 0.96, spin: 'tumble', twinkle: 0.0, fade: true },
  runes:              { shape: 'rune',    count: 14, size: 0.13,  vy: 0.12,  sway: 0.08, colors: [0xb98aff, 0x9a6ad8, 0xcbb0ff], glow: true,  opacity: 0.95, spin: 'slow',   twinkle: 0.45, fade: true },
  'phoenix-feathers': { shape: 'feather', count: 16, size: 0.2,   vy: 0.16,  sway: 0.24, colors: [0xff7a2a, 0xffd166, 0xff9a3a], glow: true,  opacity: 0.95, spin: 'tumble', twinkle: 0.3,  fade: true },
  // shadows: plain alpha-blended dark orbs at opacity 0.55 blended straight
  // into a near-black tile background were nearly invisible (low contrast on
  // low contrast). NOTE: a first pass tried additive+bright — with 18
  // overlapping instances that stacked into a blown-out white blob, losing
  // the "shadow" read entirely. This keeps solid (non-additive) blending so
  // instances don't stack-brighten, but lifts the palette to a lighter,
  // more saturated violet-grey (clearly non-black) with brighter violet
  // glints mixed in for the "edge glow" accent — reads as distinct dark
  // wisps, not a black-on-black smear or a bright glob.
  shadows:            { shape: 'orb',     count: 18, size: 0.15,  vy: 0.05,  sway: 0.18, colors: [0x584a80, 0x3a2f5a, 0x8a6fd0], glow: false, opacity: 0.88, spin: 'none',   twinkle: 0.35, fade: true },
  coins:              { shape: 'coin',    count: 16, size: 0.14,  vy: -0.28, sway: 0.1,  colors: [0xf5d166, 0xffe08a, 0xe0b84a], glow: false, opacity: 1.0,  spin: 'coin',   twinkle: 0.2,  fade: true },
};

/** A jagged lightning-bolt ribbon (unit-ish, in the local XY plane), built once
 *  per lightning emitter and instanced. Thin in Z so it reads as a flat bolt. */
function makeBoltGeometry(): THREE.BufferGeometry {
  const pts: [number, number][] = [[0.0, 0.5], [0.16, 0.16], [-0.08, -0.02], [0.12, -0.24], [-0.05, -0.5]];
  // half-width of the ribbon — widened from 0.05 so the jagged arc reads as
  // a BOLT silhouette instead of a hairline that vanishes to a faint dot.
  const hw = 0.075;
  const verts: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const [x1, y1] = pts[i]!;
    const [x2, y2] = pts[i + 1]!;
    const base = verts.length / 3;
    verts.push(x1 - hw, y1, 0, x1 + hw, y1, 0, x2 - hw, y2, 0, x2 + hw, y2, 0);
    idx.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
  g.setIndex(idx);
  return g;
}

/** Base geometry + relative scale vector for a mote shape. */
function particleBaseGeo(
  b: CharPartCtx,
  shape: ParticleShape,
): { geo: THREE.BufferGeometry; sv: [number, number, number] } {
  switch (shape) {
    case 'flake':    return { geo: b.g.sph,  sv: [1, 0.45, 1] };
    case 'streak':   return { geo: b.g.box,  sv: [0.32, 1.9, 0.32] };
    case 'card':     return { geo: b.g.box,  sv: [0.72, 1.0, 0.09] };
    // petals: a FLAT ellipse disc (a squashed cylinder, non-uniform x/z),
    // not a squashed sphere — reads as a thin petal chip, not a little ball.
    case 'petal':    return { geo: b.g.cyl8, sv: [1.0, 0.06, 0.65] };
    case 'coin':     return { geo: b.g.cyl8, sv: [1, 0.13, 1] };
    case 'feather':  return { geo: b.g.cone, sv: [0.38, 1.7, 0.38] };
    case 'rune':     return { geo: b.g.box,  sv: [1, 1, 0.14] };
    case 'bolt':     { const g = makeBoltGeometry(); b.own(g); return { geo: g, sv: [0.7, 1, 1] }; }
    // embers: a tapered flame-drop cone (not a round orb) so it reads as a
    // glowing teardrop rising off the fire, not a dot.
    case 'teardrop': return { geo: b.g.cone, sv: [0.5, 1, 0.5] };
    // gold sparks: an elongated bipyramid (octahedron stretched on Y) — an
    // actual faceted diamond silhouette, not a thin streak.
    case 'diamond': {
      const g = new THREE.OctahedronGeometry(0.5, 0);
      b.own(g);
      return { geo: g, sv: [0.45, 1.3, 0.45] };
    }
    // confetti: a flat SQUARE chip (near-equal width/height), distinct from
    // card shimmer's rectangular card silhouette.
    case 'square':   return { geo: b.g.box,  sv: [0.85, 0.85, 0.09] };
    case 'orb':
    default:         return { geo: b.g.sph,  sv: [1, 1, 1] };
  }
}

/**
 * Build a 3D AMBIENT PARTICLE system around the character for an `icon.particles`
 * cosmetic (or repurposed seat-particle). One {@link THREE.InstancedMesh} of
 * themed, name-accurate motes (see {@link PARTICLE_THEME}) drifting/tumbling/
 * spinning within a cylinder around the figure — ONE draw call for the whole
 * cloud, ticked via {@link CharPartCtx.addFx}. Renders IN-GAME + in the lobby
 * avatars (not just the locker); `opts.tier: 'lite'` thins the count for fast
 * gameplay and `opts.reduced` damps motion + count for reduced-motion / low
 * tiers. Attaches under `opts.parent` (the figure ROOT). Unknown / none → null.
 */
/**
 * BESPOKE lightning strike — a hand-built jagged bolt beside the figure,
 * replacing the generic instanced-mote approach for this theme (a swarm of
 * tiny thin bolts couldn't be tuned to read as an actual STRIKE in an 84px
 * still). Four thin emissive box segments zigzag from above head-height to
 * the floor, capped with a bright impact flash. `tick` holds a HIGH duty
 * cycle (mostly lit, briefly dimmer) so a single still almost always catches
 * it — deliberately NOT a rare flash.
 */
function buildLightningBolt(
  b: CharPartCtx,
  opts: { parent: THREE.Object3D; reduced?: boolean },
): FigureFx {
  const grp = new THREE.Group();
  grp.name = 'tt-lightning';
  opts.parent.add(grp);
  const mat = b.mk('dark', { color: 0xcfe8ff, emissive: 0xcfe8ff, emi: 1.2, flat: false });
  // jagged path from above head-height down to the floor, offset beside the
  // figure (not over the body) so it reads as a strike, not a smudge on it.
  const pts: [number, number][] = [[0.5, 2.05], [0.62, 1.5], [0.42, 0.95], [0.58, 0.42], [0.48, 0.0]];
  for (let i = 0; i < pts.length - 1; i++) {
    const [x1, y1] = pts[i]!;
    const [x2, y2] = pts[i + 1]!;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    const ang = Math.atan2(-dx, dy);
    b.part(b.g.box, mat, { x: (x1 + x2) / 2, y: (y1 + y2) / 2, z: -0.15, sx: 0.055, sy: len, sz: 0.03, rz: ang, noShell: true, shadow: false }, grp);
  }
  // bright impact flash pooling where the bolt meets the floor
  const flashGeo = new THREE.CircleGeometry(0.22, 24);
  b.own(flashGeo);
  const flashMat = b.basic(0xe8f4ff, { transparent: true, opacity: 0.8 });
  flashMat.blending = THREE.AdditiveBlending;
  flashMat.depthWrite = false;
  flashMat.toneMapped = false;
  const flash = new THREE.Mesh(flashGeo, flashMat);
  flash.rotation.x = -Math.PI / 2;
  flash.position.set(0.48, 0.03, 0);
  flash.renderOrder = -1;
  grp.add(flash);
  const motion = (opts.reduced ?? false) ? 0.4 : 1;
  const tick = (_dt: number, t: number): void => {
    const on = (Math.sin(t * 3.1 * motion) + Math.sin(t * 5.3 * motion + 1.7)) > -0.6 ? 1 : 0.35;
    mat.emissiveIntensity = on * 1.2;
    flashMat.opacity = 0.5 + on * 0.35;
  };
  return { group: grp, tick };
}

/**
 * BESPOKE shadow wisps — three distinct crescent shapes (dark violet body +
 * a bright violet EDGE rim) slowly orbiting the figure at staggered heights,
 * replacing the generic instanced-orb-cloud approach (which either blended
 * invisibly into the dark tile or, additive, blew out into a white blob).
 * The bright rim is what separates each wisp from the dark backdrop.
 */
function buildShadowWisps(
  b: CharPartCtx,
  opts: { parent: THREE.Object3D; reduced?: boolean },
): FigureFx {
  const grp = new THREE.Group();
  grp.name = 'tt-shadow-wisps';
  opts.parent.add(grp);
  const motion = (opts.reduced ?? false) ? 0.35 : 1;
  const bodyMat = b.mk('dark', { color: 0x3a2452, flat: false });
  const rimMat = b.mk('dark', { color: 0x9a6ad8, emissive: 0x9a6ad8, emi: 1.0, flat: false });
  const rimGeo = new THREE.TorusGeometry(0.16, 0.02, 6, 16, Math.PI * 1.15);
  b.own(rimGeo);
  // [orbit radius, height, phase] — staggered TORSO-band heights (below the
  // face, which a 1.3+ height collided with — one wisp read as a headband
  // draped over the face) and a radius wider than the body's shoulder width
  // so each wisp clears the silhouette and peeks out distinctly.
  const cfg: [number, number, number][] = [[0.78, 0.95, 0.5], [0.82, 0.6, 2.6], [0.74, 0.28, 4.6]];
  const wisps = cfg.map(([r, y, phase]) => {
    // seed the STARTING spread from `phase` (a = phase at t=0) rather than
    // leaving x/z at the origin — the offscreen locker/shop thumbnail baker
    // never calls Figure.tick() (it captures one static built pose), so
    // anything whose position only exists inside a tick callback renders
    // stacked at the origin (hidden inside the body) in every baked tile.
    const x0 = Math.sin(phase) * r;
    const z0 = Math.cos(phase) * r * 0.7 - 0.05;
    const body = b.part(b.g.sph, bodyMat, { x: x0, y, z: z0, sx: 0.24, sy: 0.32, sz: 0.1, noShell: true, shadow: false }, grp);
    const rim = new THREE.Mesh(rimGeo, rimMat);
    rim.position.set(x0, y, z0 + 0.02);
    grp.add(rim);
    return { body, rim, r, y, speed: 0.32 * (0.85 + (phase % 1) * 0.3), phase };
  });
  const tick = (_dt: number, t: number): void => {
    for (const w of wisps) {
      const a = t * w.speed * motion + w.phase;
      const x = Math.sin(a) * w.r;
      const z = Math.cos(a) * w.r * 0.7 - 0.05;
      w.body.position.set(x, w.y, z);
      w.body.rotation.y = a;
      w.rim.position.set(x, w.y, z + 0.02);
      w.rim.rotation.y = a;
    }
  };
  return { group: grp, tick };
}

/**
 * BESPOKE phoenix feathers (MYTHIC) — 6 hand-built layered feather blades
 * (fiery edge + warm core) orbiting the figure at staggered heights, each
 * trailing a small additive wisp, replacing the generic instanced-mote pool.
 * The instanced pool only ever sets per-instance transforms inside its `tick`
 * — invisible in the offscreen locker/shop tile baker, which never calls it
 * (see {@link buildShadowWisps}'s note). Positions/opacities here are seeded
 * at CONSTRUCTION so the "burning feathers float around you" read shows up
 * in the static baked tile; tick adds a gentle rise/loop + flicker on top.
 */
function buildPhoenixFeathers(
  b: CharPartCtx,
  opts: { parent: THREE.Object3D; reduced?: boolean },
): FigureFx {
  const grp = new THREE.Group();
  grp.name = 'tt-phoenix-feathers';
  opts.parent.add(grp);
  const motion = (opts.reduced ?? false) ? 0.4 : 1;
  // soft warm additive glow POOL under the figure's feet — the "wow glow" a
  // tile needs at a glance, with the feather silhouettes reading on top of it.
  const poolGeo = new THREE.CircleGeometry(0.62, 28);
  b.own(poolGeo);
  const poolMat = b.basic(0xff7a2a, { transparent: true, opacity: 0.55 });
  poolMat.blending = THREE.AdditiveBlending;
  poolMat.depthWrite = false;
  poolMat.toneMapped = false;
  const pool = new THREE.Mesh(poolGeo, poolMat);
  pool.rotation.x = -Math.PI / 2;
  pool.position.y = 0.03;
  pool.renderOrder = -2;
  grp.add(pool);
  const edgeMat = b.mk('dark', { color: 0xff5a1e, emissive: 0xff5a1e, emi: 1.1, flat: false }); // hot orange edge
  const coreMat = b.mk('dark', { color: 0xffe066, emissive: 0xffc23a, emi: 1.2, flat: false }); // bright gold core
  const trailMat = b.basic(0xffae4a, { transparent: true, opacity: 0.5 });
  trailMat.blending = THREE.AdditiveBlending;
  trailMat.depthWrite = false;
  trailMat.toneMapped = false;
  // [orbit radius, height, phase] — 7 feathers staggered around the figure,
  // seeded so each starts at a distinct visible spot (not stacked at the
  // origin like the instanced pool would be in a static capture).
  const cfg: [number, number, number][] = [
    [0.85, 1.55, 0.2], [0.92, 1.05, 1.1], [0.78, 0.55, 2.0], [0.95, 1.85, 2.9],
    [0.82, 0.3, 3.8], [0.7, 1.3, 4.7], [0.88, 0.8, 5.6],
  ];
  // scaled ~1.8x from the first pass — at 84px the feathers read as sparse
  // small ticks unless they're this big.
  const feathers = cfg.map(([r, y, phase], i) => {
    const x0 = Math.sin(phase) * r;
    const z0 = Math.cos(phase) * r * 0.7 - 0.05;
    const quill = new THREE.Group();
    quill.position.set(x0, y, z0);
    quill.rotation.z = Math.sin(phase) * 0.3;
    grp.add(quill);
    b.part(b.g.cone, edgeMat, { y: 0, sx: 0.135, sy: 0.576, sz: 0.135, noShell: true, shadow: false }, quill); // fiery blade
    b.part(b.g.cone, coreMat, { y: -0.036, z: 0.018, sx: 0.072, sy: 0.36, sz: 0.072, noShell: true, shadow: false }, quill); // warm core
    b.part(b.g.sph, trailMat, { y: -0.36, sx: 0.054, sy: 0.162, sz: 0.054, noShell: true, shadow: false }, quill); // trailing wisp
    return { quill, r, y, phase, bob: (i % 3) * 0.6 };
  });
  const tick = (_dt: number, t: number): void => {
    for (const f of feathers) {
      const a = f.phase + t * 0.22 * motion;
      const bobY = f.y + Math.sin(t * 1.1 * motion + f.bob) * 0.08;
      f.quill.position.set(Math.sin(a) * f.r, bobY, Math.cos(a) * f.r * 0.7 - 0.05);
      f.quill.rotation.y = a;
    }
    edgeMat.emissiveIntensity = 0.85 + 0.35 * Math.sin(t * 3.2);
    coreMat.emissiveIntensity = 0.9 + 0.35 * Math.sin(t * 3.2 + 0.6);
    poolMat.opacity = 0.5 + 0.2 * (0.5 + 0.5 * Math.sin(t * 2.4));
  };
  return { group: grp, tick };
}

export function buildParticles(
  b: CharPartCtx,
  particles: number | string | undefined,
  opts: { parent: THREE.Object3D; tier?: 'full' | 'lite'; reduced?: boolean },
): FigureFx | null {
  const id = typeof particles === 'string' ? particles : undefined;
  if (!id || id === 'none' || !(id in PARTICLE_THEME)) return null;
  // BESPOKE overrides — see the builders above for why these opt out of
  // the generic instanced-mote pool below.
  if (id === 'lightning') return buildLightningBolt(b, opts);
  if (id === 'shadows') return buildShadowWisps(b, opts);
  if (id === 'phoenix-feathers') return buildPhoenixFeathers(b, opts);
  const th = PARTICLE_THEME[id]!;
  const reduced = opts.reduced ?? false;
  // Density tiers keep gameplay sane: full in the showcase/locker, ~55% in-game,
  // and thinned further (+ damped motion) for reduced-motion / mobile.
  const tierMul = opts.tier === 'lite' ? 0.55 : 1;
  const redMul = reduced ? 0.6 : 1;
  const n = Math.max(6, Math.round(th.count * tierMul * redMul));
  const motion = reduced ? 0.4 : 1;

  const R = 0.82;
  const YMIN = 0.12;
  const YMAX = 2.15;
  const span = YMAX - YMIN;

  const { geo, sv } = particleBaseGeo(b, th.shape);
  // Flat shapes (cards, confetti coins, runes, lightning bolts) are thin quads/
  // ribbons that would vanish edge-on or when their single winding faces away —
  // render them DOUBLE-SIDED so they always read as they tumble/spin.
  const flat = th.shape === 'card' || th.shape === 'coin' || th.shape === 'rune' || th.shape === 'bolt';
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffffff, // per-instance color drives the hue; white base keeps it true
    transparent: true,
    opacity: th.opacity,
    depthWrite: false,
    ...(flat ? { side: THREE.DoubleSide } : {}),
    ...(th.glow ? { blending: THREE.AdditiveBlending, toneMapped: false } : {}),
  });
  b.ownMat?.(mat);
  const mesh = new THREE.InstancedMesh(geo, mat, n);
  mesh.frustumCulled = false;
  mesh.renderOrder = 2;
  // InstancedMesh owns per-instance matrix/color GPU buffers that disposing the
  // shared geometry/material won't free — hand it to the host's disposer.
  b.ownDisposable?.(mesh);
  // (parented below, alongside the under-glow pool, under one wrapper group)

  // per-instance state
  const baseX = new Float32Array(n);
  const baseZ = new Float32Array(n);
  const yPos = new Float32Array(n);
  const phase = new Float32Array(n);
  const swayF = new Float32Array(n);
  const spinP = new Float32Array(n);
  const sizeJ = new Float32Array(n);
  const tilt = new Float32Array(n);
  const col = new THREE.Color();
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = R * Math.sqrt(Math.random());
    baseX[i] = Math.cos(a) * r;
    baseZ[i] = Math.sin(a) * r * 0.7 - 0.05; // squash z so motes hug the figure
    yPos[i] = YMIN + Math.random() * span;   // spread across the whole column
    phase[i] = Math.random();
    swayF[i] = 0.7 + Math.random() * 1.2;
    spinP[i] = Math.random() * Math.PI * 2;
    sizeJ[i] = 0.75 + Math.random() * 0.5;
    tilt[i] = (Math.random() - 0.5) * 0.6;
    col.setHex(th.colors[i % th.colors.length]!);
    mesh.setColorAt(i, col);
  }
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;

  const mtx = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const pos = new THREE.Vector3();
  const scl = new THREE.Vector3();
  const S = th.size;
  const fadeBand = span * 0.28;

  // Per-instance transform math, shared by the SEED pass below and tick.
  // `y` is read (not drift-advanced — that only happens once per real frame
  // in tick) so this is safe to call with t=0 at construction time too.
  const writeInstance = (i: number, t: number): void => {
    const y = yPos[i]!;
    const ph = (t + phase[i]! * 6) * swayF[i]!;
    pos.set(
      baseX[i]! + Math.sin(ph) * th.sway * motion,
      y,
      baseZ[i]! + Math.cos(ph * 0.8) * th.sway * 0.6 * motion,
    );
    // size = base × shape-jitter × (fade at column ends | firefly blink | strobe)
    let f = sizeJ[i]!;
    if (th.fade) {
      const edge = Math.min(y - YMIN, YMAX - y) / fadeBand;
      f *= 0.35 + 0.65 * Math.min(1, Math.max(0, edge));
    }
    if (th.blink) f *= 0.3 + 0.7 * Math.max(0, Math.sin(t * 3.2 + phase[i]! * 6.28));
    // strobe: bolts hold a BRIGHT baseline (0.7×) with an occasional fuller
    // flash on top (staggered per bolt so several arcs are always crackling)
    // — a low floor here reads as "one faint spark" instead of lightning.
    if (th.strobe) f *= 0.7 + 0.3 * Math.pow(Math.max(0, Math.sin(t * 2.6 * swayF[i]! + spinP[i]!)), 2);
    scl.set(S * sv[0] * f, S * sv[1] * f, S * sv[2] * f);
    // rotation per spin mode
    switch (th.spin) {
      case 'tumble': e.set(t * 1.6 * motion + spinP[i]!, 0, t * 1.1 * motion + spinP[i]! * 0.5); break;
      case 'coin':   e.set(0, t * 3.0 * motion + spinP[i]!, tilt[i]!); break;
      case 'slow':   e.set(tilt[i]!, 0, t * 0.5 * motion + spinP[i]!); break;
      default:       e.set(tilt[i]!, 0, 0); break;
    }
    q.setFromEuler(e);
    mtx.compose(pos, q, scl);
    mesh.setMatrixAt(i, mtx);
  };

  // SEED every instance's real transform at CONSTRUCTION (t=0). An
  // InstancedMesh defaults every instance to the IDENTITY matrix until
  // `setMatrixAt` is called — and that only ever happened inside `tick`
  // below. The offscreen locker/shop thumbnail baker never calls
  // Figure.tick() (one static built-pose capture), so every generic-pool
  // theme (gold sparks, fireflies, coins, confetti, snow, petals, bubbles,
  // embers, card shimmer, runes…) was rendering all N instances stacked at
  // the figure's local origin — an accidental blob, not the real mote cloud.
  for (let i = 0; i < n; i++) writeInstance(i, 0);
  mesh.instanceMatrix.needsUpdate = true;

  // A theme-colored additive under-glow disc. Some of those accidental
  // origin-blobs above happened to look GOOD at a glance (gold sparks,
  // fireflies glowing at the feet) — this replaces that glow DELIBERATELY so
  // fixing the bug never reads as a downgrade: the tile now shows glow + the
  // real distinct motes, not just the motes alone. CALIBRATION PASS: the
  // first version was too timid (fixed honesty, lost punch) — fine-tier
  // (2) themes (Gold Sparks, Fireflies, …) now get ~1.6x radius + a much
  // bigger opacity jump so they feel as luminous as their old accidental
  // tile; uncommon (1) gets a smaller ~1.3x bump; rare (3) inherits the
  // fine-tier multiplier (already the richest base values).
  const glowTier = PARTICLE_GLOW_TIER[id] ?? 1;
  const radiusBase = 0.46 + glowTier * 0.06;
  const opacityBase = 0.14 + glowTier * 0.07;
  const radiusMul = glowTier >= 2 ? 1.6 : 1.3;
  const opacityAdd = glowTier >= 2 ? 0.19 : 0.09;
  const poolGeo = new THREE.CircleGeometry(radiusBase * radiusMul, 24);
  b.own(poolGeo);
  const poolOpacity = Math.min(0.68, opacityBase + opacityAdd);
  const poolMat = b.basic(th.colors[0]!, { transparent: true, opacity: poolOpacity });
  poolMat.blending = THREE.AdditiveBlending;
  poolMat.depthWrite = false;
  poolMat.toneMapped = false;
  const pool = new THREE.Mesh(poolGeo, poolMat);
  pool.rotation.x = -Math.PI / 2;
  pool.position.y = 0.025;
  pool.renderOrder = -2;
  pool.userData.ttDecor = true;

  const grp = new THREE.Group();
  grp.add(pool);
  grp.add(mesh);
  opts.parent.add(grp);

  const tick = (dt: number, t: number): void => {
    for (let i = 0; i < n; i++) {
      // drift + seamless wrap in the drift direction
      let y = yPos[i]! + th.vy * dt;
      if (y > YMAX) y -= span;
      else if (y < YMIN) y += span;
      yPos[i] = y;
      writeInstance(i, t);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (th.twinkle > 0) mat.opacity = th.opacity * (1 - th.twinkle * 0.5 * (0.5 + 0.5 * Math.sin(t * 4)));
    poolMat.opacity = poolOpacity * (0.85 + 0.15 * Math.sin(t * 1.5));
  };
  return { group: grp, tick };
}

/**
 * Build a colored RIM / STAND GLOW under the character for a repurposed
 * seat-bghue (a hue 0-360). A soft additive ground ring + a thin emissive
 * stand-rim tinted by the hue — the 3D echo of the old seat tile's bg-hue
 * overlay. Attaches under `opts.parent` (figure ROOT). Showcase-only. Marked
 * `userData.ttDecor` so framing ignores it.
 *
 * `opts.compact`: a small, punchy version sized for the baked locker/shop
 * TILE (the full-size ring below reads fine in the big hero preview but
 * drowns a small 84px crop) — tighter radius, brighter additive glow pool
 * under the ring so all 16 hues are clearly distinct at tile scale.
 */
export function buildRimGlow(
  b: CharPartCtx,
  hue: number | undefined,
  opts: { parent: THREE.Object3D; compact?: boolean },
): FigureFx | null {
  if (hue === undefined || hue < 0) return null;
  const color = new THREE.Color().setHSL(((hue % 360) + 360) % 360 / 360, 0.7, 0.55).getHex();
  const compact = opts.compact ?? false;
  const grp = new THREE.Group();
  grp.name = 'tt-rimglow';
  opts.parent.add(grp);
  const outer = compact ? 0.42 : 0.92;
  const inner = compact ? 0.2 : 0.62;
  // soft additive glow POOL under the ring band — reads as a lit puddle of the
  // hue instead of a thin hairline, the fix for "every hue tile looks the same".
  const glowGeo = new THREE.CircleGeometry(outer * (compact ? 1.2 : 1.0), 32);
  b.own(glowGeo);
  const glowMat = b.basic(color, { transparent: true, opacity: compact ? 0.55 : 0.28 });
  glowMat.blending = THREE.AdditiveBlending;
  glowMat.depthWrite = false;
  glowMat.toneMapped = false;
  const glow = new THREE.Mesh(glowGeo, glowMat);
  glow.rotation.x = -Math.PI / 2;
  glow.position.y = 0.03;
  glow.renderOrder = -2;
  glow.userData.ttDecor = true;
  grp.add(glow);
  const rg = new THREE.RingGeometry(inner, outer, 40);
  b.own(rg);
  const mat = b.basic(color, { transparent: true, opacity: compact ? 0.85 : 0.5 });
  const ring = new THREE.Mesh(rg, mat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.04;
  ring.renderOrder = -1;
  ring.userData.ttDecor = true;
  grp.add(ring);
  // a thin emissive stand-rim hugging the feet (gives the hue a 3D edge)
  const rimS = compact ? 0.34 : 0.72;
  const rim = b.part(b.g.cyl8, b.mk('dark', { color, emissive: color, emi: compact ? 0.85 : 0.5, flat: false }), { x: 0, y: 0.05, z: 0, sx: rimS, sy: 0.06, sz: rimS, noShell: true, shadow: false }, grp);
  rim.userData.ttDecor = true;
  const floor = compact ? 0.65 : 0.35;
  const amp = compact ? 0.2 : 0.25;
  const tick = (_dt: number, t: number): void => { mat.opacity = floor + amp * (0.5 + 0.5 * Math.sin(t * 2.2)); };
  return { group: grp, tick };
}

// ───────────────────────── head shapes (per BODY_SHAPES index) ─────────────────────────

const CY = 1.33; // head-core center Y on the humanoid rig (kept stable so
//                  game add-ons keyed to ~1.33 — lumber-tumble's bezel — align)

/** Small helper: a pointed ear/horn cone. */
function ear(b: CharPartCtx, sgn: number, x: number, y: number, z: number, w: number, h: number, tilt: number, mat: THREE.MeshToonMaterial): void {
  b.part(b.g.cone, mat, { x: sgn * x, y, z, sx: w, sy: h, sz: w, rz: sgn * tilt });
}

/**
 * Build a 3D head for a premium ARCHETYPE (ai 0-5: floating-orb, eight-legged,
 * phantom, crystal, living-flame, hexagon). Same head-only approach as the 19
 * base shapes — a distinct silhouette on the shared brawler body — tinted by
 * the player's icon colors. Mirrors character-render's drawBody idx>=100.
 */
function buildArchetypeHead(b: CharPartCtx, ai: number): HeadAnchor {
  const g = b.g;
  const head = (extra: MkOpts = {}): THREE.MeshToonMaterial => b.mk('skin', { color: b.colors.head, flat: false, ...extra });
  const dark = (amt = 0.3, extra: MkOpts = {}): THREE.MeshToonMaterial => b.mk('skin', { color: shadeHex(b.colors.head, amt), flat: false, ...extra });
  switch (ai) {
    case 0: { // floating-orb — smooth glossy orb + white glint + accent halo ring
      b.part(g.sph, head(), { y: CY + 0.06, s: 0.42 });
      b.part(g.sph, b.basic(0xffffff, { transparent: true, opacity: 0.7 }), { x: -0.12, y: CY + 0.2, z: 0.2, s: 0.07, noShell: true, shadow: false }); // highlight glint
      const hoop = new THREE.TorusGeometry(0.5, 0.022, 6, 26);
      b.own(hoop);
      b.part(hoop, b.mk('accent', { emissive: b.colors.accent, emi: 0.6 }), { y: CY + 0.06, rx: 1.15, noShell: true }); // orbit ring
      return { top: CY + 0.48, faceY: CY + 0.09, faceZ: 0.23, faceScale: 1.0 };
    }
    case 1: { // eight-legged — squat round body + 8 radiating legs (spider)
      b.part(g.sph, head(), { y: CY, sx: 0.42, sy: 0.34, sz: 0.44 });
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const lx = Math.sin(a);
        const lz = Math.cos(a);
        b.part(g.cyl, dark(0.3), { x: lx * 0.36, y: CY - 0.1, z: lz * 0.36, sx: 0.04, sy: 0.4, sz: 0.04, rz: lx * 1.0, rx: -lz * 1.0 });
      }
      return { top: CY + 0.18, faceY: CY + 0.05, faceZ: 0.24, faceScale: 0.95 };
    }
    case 2: { // phantom (EPIC) — SPECTRAL pale translucent wraith + trailing
      //          hem, with an ETHEREAL flicker (opacity breathes, inner glow
      //          pulses) so it reads as a living apparition, not a static blob.
      const pale = tintHex(b.colors.head, 0.6);
      const gm = b.mk('skin', { color: pale, flat: false });
      gm.transparent = true;
      gm.opacity = 0.5;
      b.part(g.sph, gm, { y: CY + 0.06, sx: 0.4, sy: 0.44, sz: 0.38 });
      b.part(g.cone, gm, { y: CY - 0.36, sx: 0.42, sy: 0.56, sz: 0.38, rx: Math.PI }); // flowing skirt
      for (const sx of [-0.22, 0, 0.22]) b.part(g.sph, gm, { x: sx, y: CY - 0.54, s: 0.14 }); // scalloped hem
      // cool inner glow so the wraith reads against a dark arena (pulses)
      const glowMat = b.basic(0xdfe8ff, { transparent: true, opacity: 0.3 });
      b.part(g.sph, glowMat, { y: CY + 0.06, z: 0.08, s: 0.18, noShell: true, shadow: false });
      b.addFx?.((_dt, t) => {
        gm.opacity = 0.42 + 0.12 * Math.sin(t * 1.6);
        glowMat.opacity = 0.2 + 0.16 * (0.5 + 0.5 * Math.sin(t * 2.0));
      });
      return { top: CY + 0.3, faceY: CY + 0.08, faceZ: 0.2, faceScale: 1 };
    }
    case 3: { // crystal — faceted twin-cone gem + inner glow, emissive accent
      b.part(g.cone, head({ flat: true, emissive: b.colors.accent, emi: 0.3 }), { y: CY + 0.18, sx: 0.4, sy: 0.4, sz: 0.4 });
      b.part(g.cone, head({ flat: true, emissive: b.colors.accent, emi: 0.3 }), { y: CY - 0.12, sx: 0.4, sy: 0.34, sz: 0.4, rx: Math.PI });
      b.part(g.sph, b.basic(0xffffff, { transparent: true, opacity: 0.4 }), { y: CY + 0.04, z: 0.1, s: 0.13, noShell: true, shadow: false }); // inner glow
      return { top: CY + 0.4, faceY: CY + 0.02, faceZ: 0.27, faceScale: 0.85 };
    }
    case 4: { // living-flame (EPIC) — FIXED fire palette (previously tinted
      //          by `b.colors.head`, so a player wearing a cool hue got a
      //          "plain dark blue cone" instead of fire — a Living Flame
      //          must look like flame no matter the wearer's color).
      //          CALIBRATION PASS: PALETTE.emberMid/emberHigh are pastel
      //          shop-signage oranges/yellows — reads as a "pale cream cone",
      //          not fire. Deep saturated orange-red base (0xd84a10) with a
      //          strong emissive gradient to a bright yellow-white tip, a
      //          bigger/brighter core, and 2 small flame-lick cones breaking
      //          the silhouette so it isn't one smooth cone. Flickers live.
      const baseMat = b.mk('dark', { color: 0xd84a10, emissive: 0xd84a10, emi: 0.85, flat: false });
      const tipMat = b.mk('dark', { color: 0xffcf30, emissive: 0xffb400, emi: 1.3, flat: false });
      const coreMat = b.mk('dark', { color: 0xfff3b0, emissive: 0xffe066, emi: 1.9, flat: false });
      b.part(g.sph, baseMat, { y: CY - 0.13, sx: 0.36, sy: 0.28, sz: 0.36, noShell: true, shadow: false }); // wide flame-base bulge
      const outer = b.part(g.cone, baseMat, { y: CY + 0.11, sx: 0.33, sy: 0.64, sz: 0.33, noShell: true, shadow: false }); // tapering body
      const inner = b.part(g.cone, tipMat, { y: CY + 0.18, sx: 0.18, sy: 0.46, sz: 0.18, noShell: true, shadow: false }); // bright tip
      b.part(g.sph, coreMat, { y: CY + 0.08, s: 0.17, noShell: true, shadow: false }); // inner core glow (bigger, brighter)
      for (const [sx, sy_, rz] of [[-0.19, -0.02, -0.55], [0.2, 0.06, 0.55]] as [number, number, number][]) // flame-lick cones
        b.part(g.cone, tipMat, { x: sx, y: CY + sy_, z: 0.05, sx: 0.1, sy: 0.26, sz: 0.1, rz, noShell: true, shadow: false });
      b.addFx?.((_dt, t) => {
        const f = 0.86 + 0.16 * Math.sin(t * 9) + 0.08 * Math.sin(t * 17); // chaotic flicker
        outer.scale.y = 0.64 * f;
        inner.scale.y = 0.46 * f;
        coreMat.emissiveIntensity = 1.7 + 0.5 * (0.5 + 0.5 * Math.sin(t * 6));
      });
      return { top: CY + 0.52, faceY: CY - 0.04, faceZ: 0.16, faceScale: 0.8 };
    }
    case 5: { // hexagon (EPIC) — faceted 6-sided gem-prism head + beveled inner
      //          hex + emissive corner studs framing the face + a bright glint.
      //          The inner hex + studs are FIXED cool-cyan emissive (not
      //          player-tinted) so it reads as a cut jewel on every wearer —
      //          only the outer prism keeps the player's identity color.
      b.part(g.cyl, head({ flat: true }), { y: CY, sx: 0.46, sy: 0.32, sz: 0.46, rx: Math.PI / 2 });
      const edgeMat = b.mk('dark', { color: 0x7ce8ff, emissive: 0x7ce8ff, emi: 0.9, flat: true });
      b.part(g.cyl, edgeMat, { y: CY, z: 0.05, sx: 0.34, sy: 0.2, sz: 0.34, rx: Math.PI / 2, noShell: true }); // beveled inner hex, cool-cyan edge
      const studMat = b.mk('accent', { color: 0x9df4ff, emissive: 0x7ce8ff, emi: 0.9, flat: true });
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
        b.part(g.sph, studMat, { x: Math.cos(a) * 0.4, y: CY + Math.sin(a) * 0.28, z: 0.16, s: 0.04, noShell: true });
      }
      b.part(g.sph, b.basic(0xffffff, { transparent: true, opacity: 0.45 }), { x: -0.14, y: CY + 0.14, z: 0.2, s: 0.045, noShell: true, shadow: false }); // glint
      b.addFx?.((_dt, t) => {
        edgeMat.emissiveIntensity = 0.7 + 0.35 * (0.5 + 0.5 * Math.sin(t * 2.3));
        studMat.emissiveIntensity = 0.5 + 0.35 * (0.5 + 0.5 * Math.sin(t * 2.5));
      });
      return { top: CY + 0.26, faceY: CY + 0.02, faceZ: 0.22, faceScale: 0.95 };
    }
    default:
      b.part(g.sph, head(), { y: CY, s: 0.4 });
      return { top: CY + 0.18, faceY: CY + 0.04, faceZ: 0.19, faceScale: 1 };
  }
}

/**
 * Build the 3D head silhouette for BODY_SHAPES index `bi` (0 blob … 18
 * scroll), attached to the figure `core`. Returns the {@link HeadAnchor} the
 * face features + hat should use. The dome wears `colors.head` (the player's
 * body color for icon figures, skin for bare slot figures).
 */
export function buildHeadShape(b: CharPartCtx, bi: number): HeadAnchor {
  // Premium Mythic/Rare archetypes live in a SEPARATE index space (100-105,
  // mirroring character-render's drawBody idx>=100 branches + the locker's
  // BODY_ARCHETYPES). Route them BEFORE the wrap so body=100 doesn't collapse
  // onto BODY_SHAPES[100 % 19]. Unknown >=100 ids wrap within the 6 archetypes.
  if (bi >= ARCHETYPE_BASE) return buildArchetypeHead(b, wrap(bi - ARCHETYPE_BASE, ARCHETYPE_COUNT));
  const idx = wrap(bi, BODY_COUNT);
  const g = b.g;
  const head = (extra: MkOpts = {}): THREE.MeshToonMaterial => b.mk('skin', { color: b.colors.head, flat: false, ...extra });
  const dark = (amt = 0.4, extra: MkOpts = {}): THREE.MeshToonMaterial =>
    b.mk('skin', { color: shadeHex(b.colors.head, amt), flat: false, ...extra });
  const acc = (extra: MkOpts = {}): THREE.MeshToonMaterial => b.mk('accent', extra);

  switch (idx) {
    case 1: { // tall — vertical egg (+ a soft underside shade for volume)
      b.part(g.sph, head(), { y: CY + 0.04, sx: 0.34, sy: 0.46, sz: 0.34 });
      b.part(g.sph, dark(0.16), { y: CY - 0.14, z: 0.0, sx: 0.3, sy: 0.16, sz: 0.3, noShell: true, shadow: false }); // jaw shadow
      return { top: CY + 0.46, faceY: CY + 0.06, faceZ: 0.18, faceScale: 1 };
    }
    case 2: { // cat — round head + pointed ears + muzzle
      b.part(g.sph, head(), { y: CY, s: 0.4 });
      for (const sgn of [-1, 1]) ear(b, sgn, 0.15, CY + 0.22, -0.02, 0.14, 0.26, 0.2, head());
      for (const sgn of [-1, 1]) ear(b, sgn, 0.15, CY + 0.23, -0.01, 0.07, 0.16, 0.2, acc()); // inner ear
      b.part(g.sph, dark(0.18), { y: CY - 0.05, z: 0.16, sx: 0.18, sy: 0.13, sz: 0.12 }); // muzzle
      b.part(g.sph, b.mk('dark', { flat: false }), { y: CY - 0.02, z: 0.24, s: 0.05, noShell: true }); // nose
      return { top: CY + 0.2, faceY: CY + 0.06, faceZ: 0.2, faceScale: 0.92 };
    }
    case 3: { // bear — round head + round ears + snout
      b.part(g.sph, head(), { y: CY, s: 0.42 });
      for (const sgn of [-1, 1]) b.part(g.sph, head(), { x: sgn * 0.18, y: CY + 0.2, z: -0.02, s: 0.16 });
      b.part(g.sph, dark(0.2), { y: CY - 0.06, z: 0.17, sx: 0.2, sy: 0.15, sz: 0.14 }); // snout
      b.part(g.sph, b.mk('dark', { flat: false }), { y: CY - 0.02, z: 0.26, s: 0.06, noShell: true }); // nose
      return { top: CY + 0.2, faceY: CY + 0.07, faceZ: 0.21, faceScale: 0.95 };
    }
    case 4: { // pumpkin — squashed ribbed gourd + stem
      b.part(g.sph, head(), { y: CY, sx: 0.46, sy: 0.38, sz: 0.42 });
      for (const sgn of [-1, 1]) b.part(g.sph, dark(0.16), { x: sgn * 0.26, y: CY, sx: 0.1, sy: 0.36, sz: 0.32 }); // side ribs
      b.part(g.cyl, b.mk('dark', { color: PALETTE.woodMid }), { y: CY + 0.36, sx: 0.06, sy: 0.16, sz: 0.06, rz: 0.2 }); // stem
      return { top: CY + 0.42, faceY: CY + 0.04, faceZ: 0.22, faceScale: 1.05 };
    }
    case 5: { // ghost — flowy translucent sheet
      const gm = head({ flat: false });
      gm.transparent = true;
      gm.opacity = 0.82;
      b.part(g.sph, gm, { y: CY + 0.04, sx: 0.38, sy: 0.42, sz: 0.36 });
      b.part(g.cone, gm, { y: CY - 0.34, sx: 0.4, sy: 0.5, sz: 0.36, rx: Math.PI }); // trailing skirt
      for (const sx of [-0.2, 0, 0.2]) b.part(g.sph, gm, { x: sx, y: CY - 0.5, z: 0.0, s: 0.13 }); // scalloped hem
      return { top: CY + 0.26, faceY: CY + 0.06, faceZ: 0.2, faceScale: 1 };
    }
    case 6: { // robot — boxy head + antenna + bolts + visor
      b.part(g.box, head(), { y: CY, sx: 0.4, sy: 0.36, sz: 0.36 });
      b.part(g.box, b.mk('dark'), { y: CY + 0.04, z: 0.18, sx: 0.32, sy: 0.12, sz: 0.04 }); // visor recess
      for (const sgn of [-1, 1]) b.part(g.cyl, b.mk('dark', { color: PALETTE.brass }), { x: sgn * 0.22, y: CY, z: 0.0, sx: 0.05, sy: 0.06, sz: 0.05, rz: Math.PI / 2 }); // ear bolts
      b.part(g.cyl, b.mk('dark', { color: PALETTE.brass }), { y: CY + 0.26, sx: 0.03, sy: 0.16, sz: 0.03 }); // antenna
      b.part(g.sph, acc({ emissive: b.colors.accent, emi: 0.9 }), { y: CY + 0.36, s: 0.07, noShell: true }); // antenna bulb
      return { top: CY + 0.2, faceY: CY + 0.04, faceZ: 0.19, faceScale: 0.95 };
    }
    case 7: { // mushroom — wide cap + spots + cream stalk
      b.part(g.cyl, b.mk('skin', { color: PALETTE.parchment, flat: false }), { y: CY - 0.08, sx: 0.24, sy: 0.34, sz: 0.24 }); // stalk
      b.part(g.sph, head(), { y: CY + 0.16, sx: 0.5, sy: 0.34, sz: 0.5 }); // cap (top hemisphere reads via squash)
      const spot = b.mk('accent', { color: 0xf4ece0 });
      for (const [sx, sz] of [[-0.22, 0.12], [0.2, 0.1], [0.04, 0.26], [0.26, -0.1]] as const)
        b.part(g.sph, spot, { x: sx, y: CY + 0.26, z: sz, s: 0.09, sy: 0.04, noShell: true }); // cap spots
      return { top: CY + 0.34, faceY: CY - 0.1, faceZ: 0.2, faceScale: 0.85 };
    }
    case 8: { // solstice-bearer (MYTHIC sun-king) — head + radiant gilded
      //          sun-crown collar + pulsing molten brow-gem + drifting rising
      //          embers. Signature animated mythic: clearly out-classes commons.
      b.part(g.sph, head(), { y: CY, s: 0.4 });
      // radiant sun-collar: gilded emissive spikes fanning up-and-OUT round the
      // sides + back of the crown (front arc skipped so the face stays clear).
      // One shared material — reads as a burning solar diadem.
      const collarMat = b.mk('accent', { color: PALETTE.gold, emissive: PALETTE.emberMid, emi: 0.85, flat: true });
      ringOf(10, 0.33, (x, z) => {
        if (z > 0.12) return; // keep the face (front) clear
        b.part(g.cone, collarMat, { x, z: z - 0.02, y: CY + 0.06, sx: 0.06, sy: 0.24, sz: 0.06, rz: x * 1.5, rx: -z * 1.5 });
      });
      // molten brow sun-gem (faceted) — pulses.
      const gemMat = b.mk('accent', { color: PALETTE.emberHigh, emissive: PALETTE.emberMid, emi: 1.6, flat: true });
      b.part(g.sph, gemMat, { y: CY + 0.15, z: 0.17, s: 0.12, noShell: true });
      // drifting rising embers (basic bright motes — glow under bloom, fade up).
      const embers = [-0.16, 0.0, 0.16, 0.08].map((sx, i) => {
        const mat = b.basic(PALETTE.emberHigh, { transparent: true, opacity: 0.85 });
        const mesh = b.part(g.sph, mat, { x: sx, y: CY + 0.3, z: -0.02, s: 0.035, noShell: true, shadow: false });
        return { mesh, mat, sx, phase: i / 4 };
      });
      b.addFx?.((_dt, t) => {
        gemMat.emissiveIntensity = 1.25 + 0.55 * Math.sin(t * 3.0);
        collarMat.emissiveIntensity = 0.7 + 0.3 * Math.sin(t * 3.0 + 1.2);
        for (const e of embers) {
          const p = (t * 0.5 + e.phase) % 1; // 0..1 rising loop
          e.mesh.position.y = CY + 0.26 + p * 0.36;
          e.mesh.position.x = e.sx + Math.sin(t * 2 + e.phase * 6) * 0.03;
          e.mat.opacity = 0.85 * (1 - p);
        }
      });
      return { top: CY + 0.24, faceY: CY + 0.04, faceZ: 0.2, faceScale: 1 };
    }
    case 9: { // fox — angular head + snout + large ears
      b.part(g.sph, head(), { y: CY, sx: 0.38, sy: 0.36, sz: 0.4 });
      b.part(g.cone, head(), { y: CY - 0.04, z: 0.26, sx: 0.16, sy: 0.3, sz: 0.16, rx: Math.PI / 2 }); // snout
      b.part(g.sph, b.mk('dark', { flat: false }), { y: CY - 0.04, z: 0.4, s: 0.05, noShell: true }); // nose
      for (const sgn of [-1, 1]) ear(b, sgn, 0.18, CY + 0.24, -0.02, 0.12, 0.32, 0.25, head());
      for (const sgn of [-1, 1]) ear(b, sgn, 0.18, CY + 0.25, -0.01, 0.06, 0.18, 0.25, b.mk('dark', { color: 0x1a1410 })); // dark inner
      return { top: CY + 0.24, faceY: CY + 0.07, faceZ: 0.2, faceScale: 0.88 };
    }
    case 10: { // wolf — broad head + mane ruff + ears + snout
      b.part(g.sph, head(), { y: CY, sx: 0.46, sy: 0.4, sz: 0.42 });
      ringOf(7, 0.34, (x, z, a) => b.part(g.cone, dark(0.3), { x, y: CY - 0.06, z: z * 0.8 - 0.06, s: 0.12, sy: 0.2, rz: x * 1.2, rx: -z * 0.6 - 0.3 + (a > Math.PI ? 0 : 0) })); // mane ruff
      b.part(g.cone, head(), { y: CY - 0.06, z: 0.28, sx: 0.18, sy: 0.3, sz: 0.18, rx: Math.PI / 2 }); // snout
      b.part(g.sph, b.mk('dark', { flat: false }), { y: CY - 0.06, z: 0.42, s: 0.06, noShell: true }); // nose
      for (const sgn of [-1, 1]) ear(b, sgn, 0.2, CY + 0.24, -0.04, 0.11, 0.24, 0.2, head());
      return { top: CY + 0.22, faceY: CY + 0.08, faceZ: 0.22, faceScale: 0.92 };
    }
    case 11: { // raven — sleek head + beak + back crest
      b.part(g.sph, head(), { y: CY, sx: 0.34, sy: 0.36, sz: 0.4 });
      b.part(g.cone, b.mk('dark', { color: PALETTE.gold }), { y: CY - 0.02, z: 0.28, sx: 0.1, sy: 0.26, sz: 0.1, rx: Math.PI / 2 }); // beak
      b.part(g.cone, dark(0.3), { y: CY + 0.16, z: -0.22, sx: 0.1, sy: 0.28, sz: 0.1, rx: -1.3 }); // back-swept crest
      return { top: CY + 0.2, faceY: CY + 0.07, faceZ: 0.22, faceScale: 0.9 };
    }
    case 12: { // owl — round head + big eye discs + ear tufts + beak
      b.part(g.sph, head(), { y: CY, s: 0.42 });
      const disc = b.mk('accent', { color: 0xf0e6d2, flat: false });
      for (const sgn of [-1, 1]) {
        b.part(g.cyl, disc, { x: sgn * 0.13, y: CY + 0.03, z: 0.2, sx: 0.14, sy: 0.02, sz: 0.14, rx: Math.PI / 2, noShell: true }); // eye disc
        addEye(b, b.core, sgn * 0.13, CY + 0.03, 0.24, 0.08, 0.08);
      }
      b.part(g.cone, b.mk('dark', { color: PALETTE.gold }), { y: CY - 0.06, z: 0.24, sx: 0.06, sy: 0.12, sz: 0.06, rx: Math.PI / 2, noShell: true }); // beak
      for (const sgn of [-1, 1]) ear(b, sgn, 0.22, CY + 0.22, -0.02, 0.09, 0.18, 0.35, head()); // ear tufts
      return { top: CY + 0.2, faceY: CY - 0.16, faceZ: 0.22, faceScale: 0.8 }; // mouth only — eyes are the discs
    }
    case 13: { // jellyfish (EPIC) — layered translucent bell + bioluminescent
      //          rim + breathing inner glow-core + emissive tendrils. Richer
      //          than a common: depth layers + a living pulse (animated).
      const bell = head({ flat: false });
      bell.transparent = true;
      bell.opacity = 0.58;
      b.part(g.sph, bell, { y: CY + 0.08, sx: 0.46, sy: 0.42, sz: 0.46 });
      b.part(g.cyl, bell, { y: CY - 0.06, sx: 0.42, sy: 0.18, sz: 0.42 }); // bell skirt
      // inner accent dome for depth through the translucent bell
      const inner = b.mk('accent', { flat: false });
      inner.transparent = true;
      inner.opacity = 0.5;
      b.part(g.sph, inner, { y: CY + 0.06, sx: 0.32, sy: 0.3, sz: 0.32, noShell: true });
      // breathing bioluminescent core
      const coreMat = b.basic(0xfff0ff, { transparent: true, opacity: 0.65 });
      const coreMesh = b.part(g.sph, coreMat, { y: CY + 0.05, s: 0.16, noShell: true, shadow: false });
      // glowing rim dots round the bell edge
      ringOf(10, 0.44, (x, z) => b.part(g.sph, b.mk('accent', { color: PALETTE.cyanPulse, emissive: PALETTE.cyanPulse, emi: 0.8, flat: false }), { x, z, y: CY - 0.02, s: 0.034, noShell: true }));
      // emissive tendrils (varied lengths) — brighter than before
      ringOf(7, 0.3, (x, z, a) => b.part(g.cyl, b.mk('accent', { emissive: b.colors.accent, emi: 0.7, flat: false }), { x, z, y: CY - 0.36, sx: 0.04, sy: 0.36 + Math.abs(Math.sin(a * 2)) * 0.16, sz: 0.04, noShell: true }));
      b.addFx?.((_dt, t) => {
        const k = 0.5 + 0.5 * Math.sin(t * 2.2);
        coreMat.opacity = 0.38 + 0.4 * k;
        coreMesh.scale.setScalar(0.15 + 0.035 * k);
      });
      return { top: CY + 0.28, faceY: CY + 0.06, faceZ: 0.28, faceScale: 0.95 };
    }
    case 14: { // living-candle — wax pillar head + flame + drips
      b.part(g.cyl, b.mk('skin', { color: PALETTE.parchment, flat: false }), { y: CY, sx: 0.3, sy: 0.5, sz: 0.3 });
      b.part(g.sph, b.mk('skin', { color: PALETTE.parchmentDim, flat: false }), { x: 0.18, y: CY + 0.02, z: 0.12, s: 0.08 }); // drip
      b.part(g.cone, b.mk('dark', { color: PALETTE.emberHigh, emissive: PALETTE.emberMid, emi: 1.7 }), { y: CY + 0.42, sx: 0.1, sy: 0.26, sz: 0.1, noShell: true }); // flame
      return { top: CY + 0.3, faceY: CY + 0.02, faceZ: 0.16, faceScale: 0.92 };
    }
    case 15: { // lantern — frame + glowing panels + handle hoop
      b.part(g.box, b.mk('dark', { color: 0x2a2018 }), { y: CY, sx: 0.42, sy: 0.5, sz: 0.42 }); // dark frame
      const glow = b.mk('accent', { color: PALETTE.emberHigh, emissive: PALETTE.emberMid, emi: 1.0, flat: false });
      for (const [ax, az] of [[0, 0.22], [0, -0.22], [0.22, 0], [-0.22, 0]] as const)
        b.part(g.box, glow, { x: ax, y: CY, z: az, sx: az ? 0.3 : 0.04, sy: 0.36, sz: az ? 0.04 : 0.3, noShell: true }); // panes
      b.part(g.cyl, b.mk('dark', { color: 0x2a2018 }), { y: CY + 0.27, sx: 0.4, sy: 0.06, sz: 0.4 }); // cap
      const hoop = new THREE.TorusGeometry(0.12, 0.022, 6, 14);
      b.own(hoop);
      b.part(hoop, b.mk('dark', { color: PALETTE.brass }), { y: CY + 0.38, rx: Math.PI / 2 }); // handle
      return { top: CY + 0.3, faceY: CY, faceZ: 0.23, faceScale: 0.9 };
    }
    case 16: { // broom — binding knot head + bristle fan + handle up
      b.part(g.sph, head(), { y: CY, s: 0.32 }); // knot/head
      b.part(g.cyl, b.mk('dark', { color: PALETTE.brass }), { y: CY + 0.05, sx: 0.3, sy: 0.1, sz: 0.3 }); // binding band
      ringOf(8, 0.16, (x, z) => b.part(g.cone, b.mk('skin', { color: PALETTE.woodLight }), { x, z, y: CY - 0.28, s: 0.07, sy: 0.4, rx: -z * 0.8, rz: x * 0.8 })); // bristles fan down
      b.part(g.cyl, b.mk('dark', { color: PALETTE.woodMid }), { y: CY + 0.4, sx: 0.05, sy: 0.5, sz: 0.05 }); // handle up
      return { top: CY + 0.18, faceY: CY + 0.02, faceZ: 0.18, faceScale: 0.8 };
    }
    case 17: { // cauldron — iron pot + rim handles + bubbling brew
      b.part(g.sph, b.mk('dark', { color: 0x26211d }), { y: CY - 0.04, sx: 0.44, sy: 0.4, sz: 0.44 });
      b.part(g.cyl, b.mk('dark', { color: 0x2c2622 }), { y: CY + 0.18, sx: 0.4, sy: 0.1, sz: 0.4 }); // rim
      for (const sgn of [-1, 1]) {
        const h = new THREE.TorusGeometry(0.08, 0.018, 6, 12);
        b.own(h);
        b.part(h, b.mk('dark', { color: 0x1c1814 }), { x: sgn * 0.42, y: CY + 0.14, rx: 0, ry: Math.PI / 2 }); // handle
      }
      const brew = b.mk('accent', { color: PALETTE.moss, emissive: PALETTE.moss, emi: 0.7, flat: false });
      for (const [bx, bz] of [[-0.1, 0.05], [0.12, -0.04], [0.0, 0.12]] as const)
        b.part(g.sph, brew, { x: bx, y: CY + 0.22, z: bz, s: 0.1, noShell: true }); // bubbles
      return { top: CY + 0.24, faceY: CY - 0.02, faceZ: 0.34, faceScale: 0.95 };
    }
    case 18: { // scroll — rolled parchment with face on the panel
      const parch = b.mk('skin', { color: PALETTE.parchment, flat: false });
      b.part(g.box, parch, { y: CY, sx: 0.4, sy: 0.42, sz: 0.08 }); // panel
      for (const sgn of [-1, 1]) b.part(g.cyl, b.mk('skin', { color: PALETTE.parchmentDim, flat: false }), { y: CY + sgn * 0.24, sx: 0.1, sy: 0.46, sz: 0.1, rz: Math.PI / 2 }); // top/bottom rolls
      return { top: CY + 0.28, faceY: CY + 0.02, faceZ: 0.06, faceScale: 0.95 };
    }
    case 0: // blob (default) — round squashed dome (+ soft underside shade)
    default: {
      b.part(g.sph, head(), { y: CY, sx: 0.4, sy: 0.37, sz: 0.4 });
      b.part(g.sph, dark(0.16), { y: CY - 0.13, z: 0.0, sx: 0.36, sy: 0.16, sz: 0.36, noShell: true, shadow: false }); // jaw shadow → volume
      return { top: CY + 0.18, faceY: CY + 0.04, faceZ: 0.19, faceScale: 1 };
    }
  }
}

/**
 * Assemble a full TRUE-3D icon head: body-shape silhouette + face relief +
 * hat, all tinted by the icon colors already on `b.colors`. Returns the head
 * anchor (e.g. for placing extra add-ons). `hat` <= 0 / undefined → no hat.
 */
export function buildIconHead(
  b: CharPartCtx,
  opts: { body?: number; face?: number; hat?: number; mask?: number | string },
): HeadAnchor {
  const anchor = buildHeadShape(b, opts.body ?? 0);
  buildFaceFeatures(b, opts.face ?? 0, anchor);
  // MASK layers ON TOP of the face (matching the 2D face→mask stack), before
  // the hat. None/unknown → no-op inside buildMask.
  buildMask(b, opts.mask, anchor);
  if (opts.hat && opts.hat > 0) buildHat(b, opts.hat, anchor.top);
  return anchor;
}

// Local copy of materials.shade (kept private; avoids importing the mutable
// color scratch out of materials for a one-liner). Darkens toward black.
const _sc = new THREE.Color();
const _bk = new THREE.Color(0, 0, 0);
const _wt = new THREE.Color(1, 1, 1);
function shadeHex(hex: number, amount: number): number {
  _sc.setHex(hex);
  return _sc.lerp(_bk, Math.min(1, Math.max(0, amount))).getHex();
}
/** Lighten a color toward white by `amount` (0 none … 1 white). */
function tintHex(hex: number, amount: number): number {
  _sc.setHex(hex);
  return _sc.lerp(_wt, Math.min(1, Math.max(0, amount))).getHex();
}
